import React, { useState, useEffect, useRef } from 'react';
import { Tooltip, Checkbox, Dropdown, Menu, Select, message, Popover } from 'antd';
import { CogIcon, PlayIcon, PlusIcon, InformationCircleIcon, ExternalLinkIcon, XIcon, ChatIcon, DocumentTextIcon, EyeIcon, ClipboardCopyIcon, PaperAirplaneIcon, DownloadIcon, ClipboardIcon, CheckIcon, TrashIcon, MailIcon, ExclamationCircleIcon, ClockIcon, DotsVerticalIcon, PencilIcon, CalculatorIcon, LightningBoltIcon, SearchIcon, FilterIcon, LockClosedIcon, ExclamationIcon, SparklesIcon } from '@heroicons/react/outline';
import { LoadingOutlined, CheckOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { colors } from '../../design-system';
import type { Creator, CreatorPlatformType } from '../Creators/creatorTypes';

import type { ResearchTableViewProps, TableColumn } from './types';
import { getLabelColorScheme } from '../../utils/labelColors';
import { PlatformIcon, OutlookIcon, GmailIcon, ShopifyIcon } from '../../design-system/components/data-display/EmailProviderIcons';
import { TableActionBar } from './TableActionBar';
import { TableType } from '../../types/tableTypes';
import creatorService, { platformService, addCreatorToProject } from '../Creators/creatorService';
import { researchTableService } from '../../services/researchTableService';
import { researchAIService } from '../../services/researchAIService';
import { OutreachCell } from './cells/OutreachCell';
import { CompanionCell } from './cells/CompanionCell';
import { TrackPostsCell } from './cells/TrackPostsCell';
import { OutreachPreviewModal } from './OutreachPreviewModal';
import { outreachService } from '../../services/outreachService';
import { PendingChangePopover } from './PendingChangePopover';
import { CellDetailsSidebar } from './CellDetailsSidebar';
import { ColumnConfigPopover } from './ColumnConfigPopover';
import type { OutreachCellData, TrackPostsCellData } from './types';
import BoringAvatar from 'boring-avatars';
import { CompanionDraftSidebar } from './CompanionDraftSidebar';
import { CompanionSettingsPopover } from './CompanionSettingsPopover';
import { WorkflowStatusColumn, type WorkflowStatus, type PendingStatusChange } from '../WorkflowContext/WorkflowStatusColumn';
import { workflowAIService, type WorkflowSuggestion } from '../../services/workflowAIService';
import { EmailSourcesColumn } from './EmailSourcesColumn';
import { useProject } from '../../contexts/ProjectContext';
import { getManagerById } from '../ProjectManager/managerPersonas';

// Utility function to format time ago
const formatTimeAgo = (timestamp: string | number | undefined): string => {
  if (!timestamp) return '';
  
  const now = Date.now();
  const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
  const diffMs = now - time;
  
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  if (seconds > 0) return `${seconds}s ago`;
  return 'just now';
};

// Utility function to format time ago in compact form (for status column)
const formatTimeAgoCompact = (timestamp: string | number | undefined): string => {
  if (!timestamp) return '';
  
  const now = Date.now();
  const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
  const diffMs = now - time;
  
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  if (seconds > 0) return `${seconds}s`;
  return 'now';
};

// Helper function to determine email status for a creator
const getCreatorEmailStatus = (creator: Creator, creatorThreadStatus: Record<string, { hasThreads: boolean; checked: boolean }>) => {
  // Check if creator has email contact info
  const hasEmail = creator.contacts?.some(c => c.type === 'email' && c.value && 
    !c.value.includes('noemail_') && 
    !c.value.includes('unknown.com') && 
    !c.value.includes('@example.com')) ||
    (creator.creator_shipping?.email && 
     !creator.creator_shipping.email.includes('noemail_') &&
     !creator.creator_shipping.email.includes('unknown.com') &&
     !creator.creator_shipping.email.includes('@example.com'));

  if (!hasEmail) {
    return { status: 'no-email', tooltip: 'No email address available' };
  }

  // Check if creator has thread status (this would come from thread checking)
  const threadStatus = creatorThreadStatus[creator.id];
  
  if (threadStatus?.checked) {
    if (threadStatus.hasThreads) {
      return { status: 'synced', tooltip: 'Email conversations synced' };
    } else {
      return { status: 'synced', tooltip: 'Email address verified, no conversations yet' };
    }
  }

  // Default to synced if we have email but no thread status checked yet
  return { status: 'synced', tooltip: 'Click to open email conversations' };
};

// Helper function to get the most recent timestamp from a row's data
const getRowLastRunTime = (rowData: Record<string, any>): { timestamp: string | number | undefined, isOld: boolean } => {
  let mostRecentTime: number | undefined;
  
  // Check all cells in the row for timestamps
  Object.values(rowData).forEach((cellData: any) => {
    const timestamps = [
      cellData?.timestamp,
      cellData?.updated_at,
      cellData?.fetch_time
    ].filter(Boolean);
    
    timestamps.forEach(ts => {
      const time = typeof ts === 'string' ? new Date(ts).getTime() : ts;
      if (time && (!mostRecentTime || time > mostRecentTime)) {
        mostRecentTime = time;
      }
    });
  });
  
  const isOld = mostRecentTime ? (Date.now() - mostRecentTime) > (24 * 60 * 60 * 1000) : false;
  
  return {
    timestamp: mostRecentTime,
    isOld
  };
};

// Helper function to get platform profile URL
const getPlatformUrl = (platform: string, handle: string): string => {
  const cleanHandle = handle.replace('@', '');
  
  switch (platform.toLowerCase()) {
    case 'tiktok':
      return `https://www.tiktok.com/@${cleanHandle}`;
    case 'instagram':
      return `https://www.instagram.com/${cleanHandle}`;
    case 'twitch':
      return `https://www.twitch.tv/${cleanHandle}`;
    default:
      return '#';
  }
};

// Helper function to get creator contact information
const getCreatorContactInfo = (creator: Creator): { email?: string } => {
  const result: { email?: string } = {};
  
  // Check contacts array for email
  if (creator.contacts && Array.isArray(creator.contacts)) {
    creator.contacts.forEach(contact => {
      if (contact.type === 'email' && contact.value) {
        result.email = contact.value;
      }
    });
  }
  
  // Fallback: check creator_shipping for email
  if (!result.email && creator.creator_shipping?.email) {
    result.email = creator.creator_shipping.email;
  }
  
  return result;
};

// Helper function to check if creator has any contact method
const hasContactMethod = (creator: Creator): boolean => {
  const contactInfo = getCreatorContactInfo(creator);
  return !!contactInfo.email;
};



// Helper function to get all creator platforms
const getCreatorPlatforms = (creator: Creator) => {
  const platforms = [];
  
  if (creator.tiktok_data?.[0]) {
    platforms.push({
      platform: 'tiktok',
      handle: creator.tiktok_data[0].handle,
      followersCount: creator.tiktok_data[0].followers_count || 0
    });
  }
  
  if (creator.instagram_data?.[0]) {
    platforms.push({
      platform: 'instagram',
      handle: creator.instagram_data[0].handle,
      followersCount: creator.instagram_data[0].followers_count || 0
    });
  }
  
  if (creator.twitch_data?.[0]) {
    platforms.push({
      platform: 'twitch',
      handle: creator.twitch_data[0].handle,
      followersCount: creator.twitch_data[0].followers_count || 0
    });
  }
  
  return platforms;
};

// Styled Components - Stripe-inspired minimal design
const TableContainer = styled.div<{ $librarySidebarOpen?: boolean; $sidebarCollapsed?: boolean }>`
  flex: 1;
  background: white;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  max-width: 100%;
  width: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
  box-sizing: border-box;
  min-width: 0;
`;

const MainContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  gap: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
`;

const TableWrapper = styled.div<{ $drawerOpen?: boolean; $librarySidebarOpen?: boolean; $sidebarCollapsed?: boolean; $mainSidebarExpanded?: boolean }>`
  flex: 1;
  overflow-x: clip;
  overflow-y: hidden;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  ${props => props.$drawerOpen && `
    flex: 0 1 auto;
    width: calc(100% - 412px);
    min-width: 300px;
    max-width: calc(100% - 412px);
  `}
`;

const TableContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: clip;
  overflow-y: hidden;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const TableAndSidebarWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  position: relative;
  overflow: hidden;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const CustomTable = styled.div<{ $sidebarOpen?: boolean }>`
  background: transparent;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  position: relative;
  min-width: 0;
  contain: layout size;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ScrollableTableContainer = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  position: relative;
  
  /* Hide native scrollbars - we'll use custom ones */
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
`;

const CustomScrollbar = styled.div<{ $vertical?: boolean }>`
  position: absolute;
  ${props => props.$vertical ? `
    right: 0;
    top: 45px;
    bottom: 0;
    width: 5px;
  ` : `
    left: 310px;
    bottom: 0;
    right: 0;
    height: 5px;
  `}
  background: transparent;
  z-index: 10;
  pointer-events: all;
`;

const CustomScrollbarThumb = styled.div<{ $vertical?: boolean }>`
  ${props => props.$vertical ? `
    width: 100%;
    min-height: 40px;
  ` : `
    height: 100%;
    min-width: 40px;
  `}
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2.5px;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.18);
  }
  
  &:active {
    background: rgba(0, 0, 0, 0.25);
  }
`;

const TableHeader = styled.div`
  display: flex;
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  z-index: 5;
  flex-shrink: 0;
  width: max-content;
  min-width: 100%;
  position: sticky;
  top: 0;
`;

// Column resize handle
const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  user-select: none;
  background: transparent;
  z-index: 10;
`;

const HeaderCell = styled.div<{ 
  $width: number; 
  $isCreator?: boolean; 
  $isCompanion?: boolean; 
  $isFirst?: boolean; 
  $isLast?: boolean; 
  $isOutreach?: boolean;
}>`
  width: ${props => props.$width}px;
  min-width: ${props => props.$width}px;
  max-width: ${props => props.$width}px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${colors.text.secondary};
  border-right: 1px solid rgba(0, 0, 0, 0.04);
  background: white;
  position: relative;
  cursor: default;
  transition: all 0.2s ease;
  
  &:hover .header-actions {
    opacity: 1;
  }
  
  ${props => props.$isLast && `
    border-right: none;
  `}
  
  ${props => props.$isFirst && `
    position: sticky;
    left: 0;
    z-index: 6;
    background: white;
  `}
  
  ${props => props.$isCreator && `
    position: sticky;
    left: 50px;
    z-index: 6;
    background: white;
  `}
  
  ${props => props.$isCompanion && `
    border-right: 1px solid rgba(0, 0, 0, 0.06);
  `}
`;

const TableBody = styled.div`
  background: transparent;
  display: flex;
  flex-direction: column;
  width: max-content;
  min-width: 100%;
  position: relative;
`;



const StyledTableRow = styled.div<{ $hasDropdown?: boolean }>`
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
  min-height: 38px;
  width: 100%;
  position: relative;
  z-index: ${props => props.$hasDropdown ? 9999 : 1};
`;


const EmailStatusArea = styled.div<{ $status?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${props => {
    switch (props.$status) {
      case 'new': return 'rgba(16, 185, 129, 0.08)'; // Subtle green
      case 'synced': return 'rgba(0, 0, 0, 0.03)'; // Subtle gray
      case 'no-email': return 'rgba(245, 158, 11, 0.08)'; // Subtle amber
      case 'error': return 'rgba(220, 38, 38, 0.08)'; // Subtle red
      default: return 'rgba(0, 0, 0, 0.03)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'new': return 'rgba(16, 185, 129, 0.2)'; // Subtle green border
      case 'synced': return 'rgba(0, 0, 0, 0.08)'; // Subtle gray border
      case 'no-email': return 'rgba(245, 158, 11, 0.2)'; // Subtle amber border
      case 'error': return 'rgba(220, 38, 38, 0.2)'; // Subtle red border
      default: return 'rgba(0, 0, 0, 0.08)';
    }
  }};
  cursor: ${props => props.$status === 'no-email' ? 'default' : 'pointer'};
  transition: all 0.15s ease;
  margin-left: 8px;
  flex-shrink: 0;
  
  &:hover {
    background: ${props => {
      switch (props.$status) {
        case 'new': return 'rgba(16, 185, 129, 0.12)';
        case 'synced': return 'rgba(0, 0, 0, 0.06)';
        case 'no-email': return 'rgba(245, 158, 11, 0.12)';
        case 'error': return 'rgba(220, 38, 38, 0.12)';
        default: return 'rgba(0, 0, 0, 0.06)';
      }
    }};
    border-color: ${props => {
      switch (props.$status) {
        case 'new': return 'rgba(16, 185, 129, 0.25)';
        case 'synced': return 'rgba(0, 0, 0, 0.12)';
        case 'no-email': return 'rgba(245, 158, 11, 0.25)';
        case 'error': return 'rgba(220, 38, 38, 0.25)';
        default: return 'rgba(0, 0, 0, 0.12)';
      }
    }};
  }
  
  .email-icon {
    width: 11px;
    height: 11px;
    color: ${props => {
      switch (props.$status) {
        case 'new': return '#10b981'; // Green
        case 'synced': return '#6b7c93'; // Gray
        case 'no-email': return '#f59e0b'; // Amber
        case 'error': return '#dc2626'; // Red
        default: return '#6b7c93';
      }
    }};
  }
`;



const CellHoverActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.12s ease;
  position: absolute;
  right: 4px;
  bottom: 4px;
  z-index: 0;
`;



const TableCell = styled.div<{ $width: number; $isCreator?: boolean; $isCompanion?: boolean; $isContact?: boolean; $isFirst?: boolean; $isLast?: boolean; $isOutreach?: boolean; $isDisabled?: boolean }>`
  width: ${props => props.$width}px;
  min-width: ${props => props.$width}px;
  max-width: ${props => props.$width}px;
  padding: 4px 10px;
  font-size: 10px;
  line-height: 1.4;
  color: #6b7c93;
  display: flex;
  align-items: center;
  overflow: hidden;
  position: relative;
  z-index: 1;
  border-right: 1px solid rgba(0, 0, 0, 0.04);
  background: transparent;
  
  &:hover ${CellHoverActions} {
    opacity: 1;
  }
  
  ${props => props.$isLast && `
    border-right: none;
  `}
  
  ${props => props.$isFirst && `
    position: sticky;
    left: 0;
    z-index: 6;
    background: white;
  `}
  
  ${props => props.$isCreator && `
    position: sticky;
    left: 50px;
    z-index: 6;
    background: white;
  `}
  
  ${props => props.$isCompanion && `
    border-right: 1px solid rgba(0, 0, 0, 0.06);
    padding: 6px;
  `}
  
  ${props => props.$isContact && `
    padding: 0;
    justify-content: center;
  `}
  
  ${props => props.$isDisabled && `
    cursor: not-allowed;
  `}
`;

const CreatorCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const CreatorAvatar = styled.div<{ $platform?: string; $hasImage?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${props => {
    if (props.$hasImage) return 'transparent';
    switch (props.$platform?.toLowerCase()) {
      case 'tiktok': return '#fe2c55';
      case 'instagram': return '#E4405F';
      case 'youtube': return '#ff0000';
      default: return 'linear-gradient(135deg, #635bff 0%, #1a73e8 100%)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
`;

const AvatarFallback = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: white;
  font-weight: 600;
  font-size: 12px;
`;

const CreatorInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CreatorName = styled.div`
  font-weight: 500;
  color: #32325d;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;


const PlatformBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const PlatformBadge = styled.a`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.03);
  cursor: pointer;
  transition: all 0.12s ease;
  text-decoration: none;
  
  &:hover {
    background: rgba(0, 0, 0, 0.08);
    transform: scale(1.1);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 2px;
  }
`;


const CellContent = styled.div<{ $loading?: boolean; $error?: boolean }>`
  min-height: 20px;
  padding: 6px 8px;
  font-size: 11px;
  line-height: 1.4;
  cursor: text;
  border-radius: 3px;
  color: ${props => 
    props.$error ? '#e25950' :
    props.$loading ? '#a3aed0' :
    !props.children || props.children === 'Click' ? '#a3aed0' :
    '#6b7c93'
  };
  word-wrap: break-word;
  flex: 1;
`;


const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  border-radius: 3px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s ease;
  
  &:hover {
    background: #fbfcfd;
    color: #6b7c93;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const AddColumnContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  cursor: pointer;
  transition: all 0.12s ease;
  margin: 0;
  background: transparent;
  padding: 0 8px;
  color: #6b7c93;
  
  &:hover {
    color: #222429;
  }
`;

const AddColumnContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #a3aed0;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const SkeletonLoader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 2px 0;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonLine = styled.div<{ $width?: string }>`
  height: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
  width: ${props => props.$width || '100%'};
  
  @keyframes skeleton-loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const CellOpenButton = styled.button`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 5px;
  cursor: pointer;
  color: #6b7c93;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  
  &:hover {
    color: #425466;
    background: rgba(255, 255, 255, 1);
    border-color: rgba(0, 0, 0, 0.12);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const CellInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  font-size: 12px;
  color: #6b7c93;
  outline: none;
  padding: 0;
  font-family: inherit;
  line-height: 1.4;
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const EmailCellContainer = styled.div<{ $isEmpty: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: text;
  padding: 2px 0;
  position: relative;
  
  &:hover {
    ${props => props.$isEmpty && `
      background: #fafbfc;
    `}
  }
`;

const EmailSearchIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 3px;
  padding: 0;
  flex-shrink: 0;
  transition: all 0.15s ease;
  
  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }
  
  &:active {
    background: #e5e7eb;
  }
`;

const TemplateCellWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
`;

const TemplateConfigCTA = styled.button`
  background: #495057;
  border: 1px solid #495057;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: all 0.15s ease;
  width: 100px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #343a40;
    border-color: #343a40;
    transform: translateY(-1px);
  }
`;

const CellWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

// Alert components (matching creator library design)
const InfoAlert = styled.div`
  background: rgba(99, 102, 241, 0.04);
  border: 1px solid rgba(99, 102, 241, 0.12);
  border-radius: 8px;
  padding: 10px 12px;
  margin: 0 12px 12px 12px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  animation: slideDown 0.2s ease;
  position: sticky;
  left: 12px;
  width: calc(100% - 24px);
  max-width: calc(100% - 24px);
  box-sizing: border-box;
  flex-shrink: 0;
  z-index: 2;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const InfoAlertIcon = styled.div`
  flex-shrink: 0;
  color: #6366f1;
  display: flex;
  align-items: center;
  padding-top: 1px;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const InfoAlertContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const InfoAlertTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #4f46e5;
  margin-bottom: 2px;
  letter-spacing: -0.01em;
`;

const InfoAlertMessage = styled.div`
  font-size: 10px;
  color: #6b7280;
  line-height: 1.5;
  letter-spacing: -0.01em;
`;

const AlertCloseButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.12s ease;
  flex-shrink: 0;
  
  svg {
    width: 12px;
    height: 12px;
  }
  
  &:hover {
    background: rgba(99, 102, 241, 0.08);
    color: #6366f1;
  }
`;

// Quick Add Creator components (matching table cell style)
const QuickAddContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  width: 100%;
  overflow: visible !important;
`;

const AtSymbol = styled.span`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 400;
  flex-shrink: 0;
`;

const QuickAddInput = styled.input<{ $status: 'idle' | 'searching' | 'found' | 'not_found' | 'adding' }>`
  border: none;
  background: transparent;
  font-size: 11px;
  color: #222429;
  outline: none;
  padding: 0;
  font-family: inherit;
  line-height: 1.4;
  flex: 1;
  min-width: 80px;
  
  &::placeholder {
    color: #a1a8b3;
    font-weight: 400;
  }
  
  &:disabled {
    opacity: 0.6;
  }
`;

const QuickAddStatusIcon = styled.div<{ $status: 'idle' | 'searching' | 'found' | 'not_found' | 'adding' }>`
  width: 10px;
  height: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 2px;
  
  svg {
    width: 10px;
    height: 10px;
    color: ${props => {
      switch (props.$status) {
        case 'searching': return colors.palette.blue[500];
        case 'found': return colors.palette.green[500];
        case 'adding': return colors.palette.green[500];
        case 'not_found': return colors.palette.red[500];
        default: return 'transparent';
      }
    }};
  }
  
  ${props => (props.$status === 'searching' || props.$status === 'adding') && `
    svg {
      animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `}
`;

const QuickAddProfileImageWrapper = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const QuickAddProfileImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 3px;
  object-fit: cover;
  display: block;
`;

const QuickAddProfileFallback = styled.div<{ $platform: string }>`
  width: 20px;
  height: 20px;
  border-radius: 3px;
  background: ${props => {
    switch (props.$platform?.toLowerCase()) {
      case 'tiktok': return '#fe2c55';
      case 'instagram': return '#E4405F';
      default: return '#6b7280';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 9px;
`;

const AddToProjectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid ${colors.palette.green[300]};
  border-radius: 3px;
  background: ${colors.palette.green[50]};
  cursor: pointer;
  transition: all 0.12s ease;
  padding: 0;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    border-color: ${colors.palette.green[400]};
    background: ${colors.palette.green[100]};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 11px;
    height: 11px;
    color: ${colors.palette.green[600]};
  }
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 520px;
  background: #ffffff;
  border: 1px solid #f3f4f6;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  max-height: 320px;
  overflow-y: auto;
  z-index: 10000;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 2px;
  }
`;

const DropdownHeader = styled.div`
  padding: 8px 12px 4px;
  font-size: 10px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 500;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
`;

const PlatformColumn = styled.div<{ $isRight?: boolean }>`
  display: flex;
  flex-direction: column;
  ${props => props.$isRight && `
    border-left: 1px solid #f9fafb;
  `}
`;

const PlatformColumnHeader = styled.div<{ $platform: 'instagram' | 'tiktok' }>`
  padding: 6px 10px;
  font-size: 10px;
  font-weight: 500;
  color: #6b7280;
  background: #fafbfc;
  display: flex;
  align-items: center;
  gap: 5px;
  border-bottom: 1px solid #f3f4f6;
  letter-spacing: 0.3px;
`;

const DropdownSkeletonItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const DropdownSkeletonCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: #f3f4f6;
`;

const DropdownSkeletonText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DropdownSkeletonLine = styled.div<{ $width?: string }>`
  height: 10px;
  background: #f3f4f6;
  border-radius: 4px;
  width: ${props => props.$width || '100%'};
`;

const SuggestionItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  transition: background 0.08s ease;
  background: ${props => props.$selected ? '#fafbfc' : 'transparent'};
  
  &:hover {
    background: #fafbfc;
  }
  
  &:active {
    background: #f3f4f6;
  }
`;

const SuggestionImage = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const SuggestionFallback = styled.div<{ $platform: string }>`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: ${props => {
    switch (props.$platform?.toLowerCase()) {
      case 'tiktok': return '#fe2c55';
      case 'instagram': return '#E4405F';
      default: return '#6b7280';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
`;

const SuggestionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SuggestionHandle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #222429;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SuggestionStats = styled.div`
  font-size: 10px;
  color: #9ca3af;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 10px;
  flex-shrink: 0;
  margin-left: 2px;
`;

const LibraryBadge = styled.span`
  font-size: 9px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 1px 4px;
  border-radius: 3px;
  margin-left: 6px;
  font-weight: 500;
  letter-spacing: 0.3px;
`;

const PlatformSelectorContainer = styled.div`
  display: flex;
  gap: 3px;
  flex-shrink: 0;
`;

const PlatformOption = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid ${props => props.$selected ? '#3b82f6' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 3px;
  background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.08)' : 'transparent'};
  cursor: pointer;
  transition: all 0.12s ease;
  padding: 0;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.$selected ? '#2563eb' : 'rgba(0, 0, 0, 0.15)'};
    background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.12)' : 'rgba(0, 0, 0, 0.02)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;


export const ResearchTableView: React.FC<ResearchTableViewProps> = (props) => {
  const {
  columns,
  rows,
  tableData,
  editingColumnId,
  onResearchCell,
  onResearchAllInColumn,
  onEditColumn,
  onSaveColumn,
  onDeleteColumn,
  onCancelEdit,
  onAddColumn,
  onReorderColumns,
  onExportCSV,
  onCopyToClipboard,
  onRefreshTable,
  onHiddenColumnsChange,
  tableName,
  tableId,
  projectId,
  onCreatorAdded,
  onUpdateCellValue,
  onOpenCreatorConversation,
  onOpenCellSidebar,
  onAcceptPendingChange,
  onDismissPendingChange,
  confirmOverwritePopover,
  onConfirmOverwrite,
  onCancelOverwrite,
  getCreatorPrimaryPlatform,
  outreachDrawerOpen = false,
  outreachDrawerContent,
  projectTables = [],
  activeTableId,
  onTabClick,
  onDeleteTable,
  onCreateTable,
  showTabBar = false,
  uploadMode = false,
  searchMode = false,
  onAddCreatorManually,
  
  // Selection mode props
  selectionMode = false,
  selectedCreators = [],
  onCreatorSelect,
  
  // Sidebar responsiveness
  librarySidebarOpen = false,
  sidebarCollapsed = false,
  mainSidebarExpanded = false,
  
  // Communication cell thread checking
  isCommunicationCellDisabled,
  isPlatformCellDisabled,
  creatorThreadStatus = {},
  
  // Shopify discount code deletion
  onDeleteShopifyDiscountCode,
  
  // Creator editing
  onEditCreator,
  onRemoveCreator,
  
  // Table requirements
  tableType,
  
  // Table context
  tableContext,
  tableGoal,
  onUpdateTableContext,
  onRunAllEmpty,
  onOpenTemplates,
  
  // Cell details sidebar
  cellSidebarOpen = false,
  onCloseCellSidebar,
  selectedCell,
  getCreatorRowData,
  onVerifyPost,
  
  // Companion settings
  currentTable,
  onUpdateCompanionSettings,
  
  // Workflow analysis queue status
  queueStatus
  } = props;

  const [editingCell, setEditingCell] = useState<{ rowId: string; columnId: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [imageErrors, setImageErrors] = React.useState<Set<string>>(new Set());
  const [containerHeight, setContainerHeight] = useState(0);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copying' | 'success' | 'error'>('idle');
  const [hoveredContactCell, setHoveredContactCell] = useState<string | null>(null);
  
  // Column resizing state (local only, resets on rerender)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  
  // Column filter state
  const [columnFilters, setColumnFilters] = useState<Record<string, { type: 'has_value' | 'is_empty' | 'contains'; value?: string }>>({});
  
  // Drag and drop state for column reordering
  const draggedElementRef = useRef<HTMLElement | null>(null);
  
  // Hidden columns state - initialized from table_config
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    const hiddenFromConfig = currentTable?.table_config?.hidden_columns || [];
    return new Set(hiddenFromConfig);
  });
  
  // Update hiddenColumns when currentTable changes (after reload)
  useEffect(() => {
    if (currentTable?.table_config?.hidden_columns) {
      console.log('🔄 [ResearchTableView] Updating hiddenColumns from currentTable:', currentTable.table_config.hidden_columns);
      setHiddenColumns(new Set(currentTable.table_config.hidden_columns));
    }
  }, [currentTable?.table_config?.hidden_columns]);
  
  // Filter visible columns
  const visibleColumns = columns.filter(column => !hiddenColumns.has(column.id));
  
  // Template preview state
  const [templatePreviews, setTemplatePreviews] = useState<Record<string, string>>({});
  const [loadingPreviews, setLoadingPreviews] = useState<Set<string>>(new Set());
  
  // Alert state
  const [showNoCreatorsAlert, setShowNoCreatorsAlert] = useState(true);
  const [showNotRunAlert, setShowNotRunAlert] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Column config popover state
  const [columnConfigPopover, setColumnConfigPopover] = useState<{
    column: TableColumn | null;
    anchorEl: HTMLElement | null;
    position?: { top: number; left: number };
  }>({ column: null, anchorEl: null });

  // Companion draft state
  const [companionDrafts, setCompanionDrafts] = useState<Map<string, string>>(new Map());
  const [companionMessages, setCompanionMessages] = useState<Map<string, Array<{
    message_id: string;
    user_type: 'USER' | 'CREATOR';
    sender: string;
    subject?: string;
    received_at: string;
    content: string;
    has_attachments?: boolean;
    is_target?: boolean;
  }>>>(new Map());
  const [companionConversations, setCompanionConversations] = useState<Map<string, Array<{
    message_id: string;
    user_type: 'USER' | 'CREATOR';
    sender: string;
    subject?: string;
    received_date_time?: string;
    content: string;
    has_attachments?: boolean;
  }>>>(new Map());
  const [companionDraftOpen, setCompanionDraftOpen] = useState(false);
  const [companionDraftCreator, setCompanionDraftCreator] = useState<Creator | null>(null);
  const [companionGeneratingDrafts, setCompanionGeneratingDrafts] = useState<Set<string>>(new Set());
  
  // Generate companion draft for a creator
  const handleGenerateDraft = async (creator: Creator) => {
    const creatorId = creator.id;
    
    // Add to generating set
    setCompanionGeneratingDrafts(prev => new Set(prev).add(creatorId));
    
    try {
      // Extract creator email from contacts or creator_shipping
      const creatorEmails: string[] = [];
      
      // Check contacts array for email
      if (creator.contacts && Array.isArray(creator.contacts)) {
        const emailContacts = creator.contacts.filter(c => c.type === 'email' && c.value && c.value.trim() !== '');
        creatorEmails.push(...emailContacts.map(c => c.value));
      }
      
      // Check creator_shipping for email (fallback)
      if (creator.creator_shipping?.email && !creatorEmails.includes(creator.creator_shipping.email)) {
        creatorEmails.push(creator.creator_shipping.email);
      }
      
      // Filter out invalid emails
      const validEmails = creatorEmails.filter(email => 
        email && 
        !email.includes('noemail_') && 
        !email.includes('unknown.com') && 
        !email.includes('@example.com')
      );
      
      console.log('🤖 [CompanionDraft] Creator emails found:', validEmails);
      
      // Prepare creator info for AI agent
      const creatorInfo = {
        id: creator.id,
        name: creator.tiktok_data?.[0]?.handle || creator.instagram_data?.[0]?.handle || 'Creator',
        handle: creator.tiktok_data?.[0]?.handle || creator.instagram_data?.[0]?.handle || '',
        platform: creator.tiktok_data?.[0] ? 'tiktok' : 'instagram',
        followers_count: creator.tiktok_data?.[0]?.followers_count || creator.instagram_data?.[0]?.followers_count
      };
      
      // Call companion AI agent with default prompt
      const response = await researchAIService.callCompanionAgent({
        prompt: "Draft a personalized message to this creator asking for the information needed to complete this table.",
        table_goal: companionTableGoal || '',
        creator_info: creatorInfo,
        creator_emails: validEmails,
        project_id: projectId,
        table_id: tableId,
        column_id: "companion"
      });
      
      if (response.success && response.result) {
        // Store the generated draft
        setCompanionDrafts(prev => new Map(prev).set(creatorId, response.result || ''));
        
        // Store messages from selected thread (for "Previous Conversation" panel)
        if (response.messages) {
          setCompanionMessages(prev => new Map(prev).set(creatorId, response.messages!));
        }
        
        // Store conversation preview if available (for future use)
        if (response.conversation_preview) {
          setCompanionConversations(prev => new Map(prev).set(creatorId, response.conversation_preview!));
        }
        
        // Open the companion sidebar with this creator and draft
        setCompanionDraftCreator(creator);
        setCompanionDraftOpen(true);
        
        console.log('🤖 [CompanionDraft] Draft generated successfully for:', creator.id);
      } else {
        console.error('🤖 [CompanionDraft] Failed to generate draft:', response.error);
        // TODO: Show error message to user
      }
      
    } catch (error) {
      console.error('🤖 [CompanionDraft] Error generating draft:', error);
      // TODO: Show error message to user
    } finally {
      // Remove from generating set
      setCompanionGeneratingDrafts(prev => {
        const newSet = new Set(prev);
        newSet.delete(creatorId);
        return newSet;
      });
    }
  };

  // Regenerate draft with feedback
  const handleRegenerateDraft = async (feedback: string) => {
    if (!companionDraftCreator) return;
    
    const creatorId = companionDraftCreator.id;
    
    try {
      // Extract creator email from contacts or creator_shipping
      const creatorEmails: string[] = [];
      
      // Check contacts array for email
      if (companionDraftCreator.contacts && Array.isArray(companionDraftCreator.contacts)) {
        const emailContacts = companionDraftCreator.contacts.filter(c => c.type === 'email' && c.value && c.value.trim() !== '');
        creatorEmails.push(...emailContacts.map(c => c.value));
      }
      
      // Check creator_shipping for email (fallback)
      if (companionDraftCreator.creator_shipping?.email && !creatorEmails.includes(companionDraftCreator.creator_shipping.email)) {
        creatorEmails.push(companionDraftCreator.creator_shipping.email);
      }
      
      // Filter out invalid emails
      const validEmails = creatorEmails.filter(email => 
        email && 
        !email.includes('noemail_') && 
        !email.includes('unknown.com') && 
        !email.includes('@example.com')
      );
      
      console.log('🤖 [CompanionDraft] Regenerate - Creator emails found:', validEmails);
      
      // Prepare creator info for AI agent
      const creatorInfo = {
        id: companionDraftCreator.id,
        name: companionDraftCreator.tiktok_data?.[0]?.handle || companionDraftCreator.instagram_data?.[0]?.handle || 'Creator',
        handle: companionDraftCreator.tiktok_data?.[0]?.handle || companionDraftCreator.instagram_data?.[0]?.handle || '',
        platform: companionDraftCreator.tiktok_data?.[0] ? 'tiktok' : 'instagram',
        followers_count: companionDraftCreator.tiktok_data?.[0]?.followers_count || companionDraftCreator.instagram_data?.[0]?.followers_count
      };
      
      // Call companion AI agent with feedback
      const response = await researchAIService.callCompanionAgent({
        prompt: `${feedback}\n\nPrevious draft: ${companionDrafts.get(creatorId) || ''}`,
        table_goal: companionTableGoal || '',
        creator_info: creatorInfo,
        creator_emails: validEmails,
        project_id: projectId,
        table_id: tableId,
        column_id: "companion"
      });
      
      if (response.success && response.result) {
        // Update the stored draft
        setCompanionDrafts(prev => new Map(prev).set(creatorId, response.result || ''));
        
        // Update messages from selected thread (for "Previous Conversation" panel)
        if (response.messages) {
          setCompanionMessages(prev => new Map(prev).set(creatorId, response.messages!));
        }
        
        // Update conversation preview if available (for future use)
        if (response.conversation_preview) {
          setCompanionConversations(prev => new Map(prev).set(creatorId, response.conversation_preview!));
        }
        
        console.log('🤖 [CompanionDraft] Draft regenerated successfully for:', creatorId);
      } else {
        throw new Error(response.error || 'Failed to regenerate draft');
      }
      
    } catch (error) {
      console.error('🤖 [CompanionDraft] Error regenerating draft:', error);
      throw error; // Re-throw for the UI to handle
    }
  };
  
  // Mark initial load as complete after data is available
  useEffect(() => {
    // Wait a bit to ensure data has rendered before showing alerts
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [rows, columns]);
  
  // Quick add creator state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddHandle, setQuickAddHandle] = useState('');
  const [quickAddVerificationStatus, setQuickAddVerificationStatus] = useState<'idle' | 'searching' | 'found' | 'not_found' | 'adding'>('idle');
  const [quickAddSearchResult, setQuickAddSearchResult] = useState<any>(null);
  const [quickAddImageError, setQuickAddImageError] = useState(false);
  const [quickAddInstagramSuggestions, setQuickAddInstagramSuggestions] = useState<any[]>([]);
  const [quickAddTikTokSuggestions, setQuickAddTikTokSuggestions] = useState<any[]>([]);
  const [showQuickAddDropdown, setShowQuickAddDropdown] = useState(false);
  const [quickAddSelectedIndex, setQuickAddSelectedIndex] = useState(-1);
  const [quickAddSelectedPlatform, setQuickAddSelectedPlatform] = useState<'instagram' | 'tiktok' | null>(null);
  const [isSearching, setIsSearching] = useState(false); // Separate state for search loading (doesn't block input)
  const lastSearchedHandleRef = useRef<string>('');
  
  // Outreach preview modal state
  const [outreachPreviewOpen, setOutreachPreviewOpen] = useState(false);
  const [outreachPreviewColumn, setOutreachPreviewColumn] = useState<TableColumn | null>(null);
  const [outreachPreviewCreator, setOutreachPreviewCreator] = useState<Creator | null>(null);
  const [outreachPreviewRowId, setOutreachPreviewRowId] = useState<string | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  
  // Companion settings state - initialize from table config
  const [companionEnabled, setCompanionEnabled] = useState(currentTable?.table_config?.companion_enabled || false);
  const [companionTableGoal, setCompanionTableGoal] = useState(currentTable?.table_config?.companion_table_goal || '');
  const [companionSettingsOpen, setCompanionSettingsOpen] = useState(false);
  
  // Get selected project for companion name
  const { selectedProject } = useProject();
  
  // Workflow filter state
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const workflowEnabled = selectedProject?.has_workflow || false;
  
  // Workflow filter handler
  const handleWorkflowFilterChange = (activeOnly: boolean) => {
    setShowActiveOnly(activeOnly);
    console.log('Workflow filter changed:', activeOnly ? 'In Progress only' : 'All creators');
  };
  
  // Workflow status management - treat exactly like regular cells
  const handleStatusChange = async (creatorId: string, newStatus: WorkflowStatus) => {
    console.log('🔄 [WorkflowStatus] Status change:', creatorId, '->', newStatus, 'in table:', tableId);
    
    // Clear AI suggestion after status change
    setAISuggestions(prev => {
      const updated = { ...prev };
      delete updated[creatorId];
      return updated;
    });
    
    // Just call onUpdateCellValue - it will handle everything (update local state + persist to backend)
    // This is exactly how regular cells work
    if (onUpdateCellValue) {
      try {
        console.log('🔄 [WorkflowStatus] Calling onUpdateCellValue for table:', tableId);
        await onUpdateCellValue(creatorId, 'workflow_status', newStatus);
        console.log(`✅ Status persisted for creator ${creatorId}: ${newStatus} in table ${tableId}`);
      } catch (error) {
        console.error('❌ Failed to persist status:', error);
      }
    } else {
      console.warn('⚠️ Missing onUpdateCellValue function');
    }
  };
  
  // AI suggestions state
  const [aiSuggestions, setAISuggestions] = useState<Record<string, WorkflowSuggestion>>({});
  const [loadingSuggestions, setLoadingSuggestions] = useState<Set<string>>(new Set());
  
  const handleRequestAISuggestion = async (creatorId: string) => {
    if (loadingSuggestions.has(creatorId) || aiSuggestions[creatorId]) return;
    
    const creator = rows.find(row => row.id === creatorId)?.creator;
    if (!creator) return;
    
    setLoadingSuggestions(prev => new Set(prev).add(creatorId));
    
    try {
      const suggestion = await workflowAIService.suggestWorkflowStatus({
        creator,
        currentStatus: (tableData[creatorId]?.workflow_status?.value as WorkflowStatus) || 'not_started',
        tableData: tableData[creatorId] || {},
        columns,
        projectContext: selectedProject?.description,
        tableGoal: currentTable?.table_config?.table_goal,
        tableId: currentTable?.id,
        projectId: selectedProject?.project_uuid
      });
      
      setAISuggestions(prev => ({
        ...prev,
        [creatorId]: suggestion
      }));
    } catch (error) {
      console.error('Failed to get AI suggestion for creator:', creatorId, error);
    } finally {
      setLoadingSuggestions(prev => {
        const updated = new Set(prev);
        updated.delete(creatorId);
        return updated;
      });
    }
  };
  
  // Sync companion settings when table config changes
  useEffect(() => {
    if (currentTable?.table_config) {
      setCompanionEnabled(currentTable.table_config.companion_enabled || false);
      setCompanionTableGoal(currentTable.table_config.companion_table_goal || '');
    }
  }, [currentTable?.table_config?.companion_enabled, currentTable?.table_config?.companion_table_goal]);
  
  // Companion settings handlers that save to backend
  const handleCompanionEnabledChange = (enabled: boolean) => {
    // Just update local state for immediate UI feedback
    setCompanionEnabled(enabled);
  };
  
  const handleCompanionSave = async (enabled: boolean, goal: string) => {
    // Save both settings to backend
    if (onUpdateCompanionSettings) {
      await onUpdateCompanionSettings(enabled, goal);
    }
  };

  
  // Email editing state
  const [editingEmailRowId, setEditingEmailRowId] = useState<string | null>(null);
  const [editingEmailValue, setEditingEmailValue] = useState<string>('');
  
  // Optimistic UI for quick-added creators
  const [optimisticCreators, setOptimisticCreators] = useState<any[]>([]);
  
  // Optimistic UI for creator updates (email, etc.)
  const [optimisticCreatorUpdates, setOptimisticCreatorUpdates] = useState<Record<string, Partial<Creator>>>({});
  
  // Column name editing state
  const [editingColumnName, setEditingColumnName] = useState<string | null>(null);
  const [editingColumnNameValue, setEditingColumnNameValue] = useState('');
  
  // Custom scrollbar state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollThumbTop, setScrollThumbTop] = useState(0);
  const [scrollThumbLeft, setScrollThumbLeft] = useState(0);
  const [scrollThumbHeight, setScrollThumbHeight] = useState(100);
  const [scrollThumbWidth, setScrollThumbWidth] = useState(100);
  const [isDraggingVertical, setIsDraggingVertical] = useState(false);
  const [isDraggingHorizontal, setIsDraggingHorizontal] = useState(false);
  
  // Update custom scrollbar positions on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const updateScrollbars = () => {
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      
      // Vertical scrollbar
      const verticalThumbHeight = Math.max(40, (clientHeight / scrollHeight) * clientHeight);
      const verticalScrollPercentage = container.scrollTop / (scrollHeight - clientHeight);
      const verticalThumbTop = verticalScrollPercentage * (clientHeight - verticalThumbHeight);
      
      setScrollThumbHeight(verticalThumbHeight);
      setScrollThumbTop(verticalThumbTop);
      
      // Horizontal scrollbar
      const horizontalThumbWidth = Math.max(40, (clientWidth / scrollWidth) * clientWidth);
      const horizontalScrollPercentage = container.scrollLeft / (scrollWidth - clientWidth);
      const horizontalThumbLeft = horizontalScrollPercentage * (clientWidth - horizontalThumbWidth);
      
      setScrollThumbWidth(horizontalThumbWidth);
      setScrollThumbLeft(horizontalThumbLeft);
    };
    
    updateScrollbars();
    container.addEventListener('scroll', updateScrollbars);
    window.addEventListener('resize', updateScrollbars);
    
    return () => {
      container.removeEventListener('scroll', updateScrollbars);
      window.removeEventListener('resize', updateScrollbars);
    };
  }, [rows, columns]);
  
  // Column resize logic
  const handleResizeStart = (columnId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnId);
    
    const startX = e.clientX;
    const startWidth = columnWidths[columnId] || 180;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(100, Math.min(400, startWidth + diff)); // Min 100px, max 400px
      setColumnWidths(prev => ({ ...prev, [columnId]: newWidth }));
    };
    
    const handleMouseUp = () => {
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Helper to get column width (from state or default)
  const getColumnWidth = (columnId: string) => {
    return columnWidths[columnId] || 180;
  };
  
  // Check if any cell in a column has been run or is loading
  const hasColumnBeenRun = (columnId: string) => {
    return Object.values(tableData).some(rowData => 
      (rowData[columnId]?.value && rowData[columnId].value.trim() !== '') ||
      rowData[columnId]?.loading
    );
  };
  
  const canColumnBeExecuted = (column: TableColumn) => {
    return column.cell_type === 'normal' || column.cell_type === 'communication' || column.cell_type === 'relationship' || column.cell_type === 'track_posts' || column.cell_type === 'companion_recommendation';
  };
  
  // Column name editing handlers
  const handleStartEditColumnName = (column: TableColumn) => {
    setEditingColumnName(column.id);
    setEditingColumnNameValue(column.name);
  };
  
  const handleSaveColumnName = (column: TableColumn) => {
    if (editingColumnNameValue.trim() && editingColumnNameValue !== column.name) {
      // Update the column with the new name
      onSaveColumn({ ...column, name: editingColumnNameValue.trim() });
    }
    setEditingColumnName(null);
    setEditingColumnNameValue('');
  };
  
  const handleCancelEditColumnName = () => {
    setEditingColumnName(null);
    setEditingColumnNameValue('');
  };

  // Column visibility handlers
  const handleHideColumn = async (columnId: string) => {
    const newHiddenColumns = new Set([...Array.from(hiddenColumns), columnId]);
    setHiddenColumns(newHiddenColumns);
    await saveHiddenColumnsToBackend(newHiddenColumns);
    onHiddenColumnsChange?.(newHiddenColumns);
    message.success('Column hidden');
  };

  const handleShowColumn = async (columnId: string) => {
    const newHiddenColumns = new Set(hiddenColumns);
    newHiddenColumns.delete(columnId);
    setHiddenColumns(newHiddenColumns);
    await saveHiddenColumnsToBackend(newHiddenColumns);
    onHiddenColumnsChange?.(newHiddenColumns);
    message.success('Column shown');
  };

  const handleShowAllColumns = async () => {
    const newHiddenColumns = new Set<string>();
    setHiddenColumns(newHiddenColumns);
    await saveHiddenColumnsToBackend(newHiddenColumns);
    onHiddenColumnsChange?.(newHiddenColumns);
    message.success('All columns shown');
  };

  const saveHiddenColumnsToBackend = async (hiddenColumnsSet: Set<string>) => {
    if (!tableId) return;
    
    try {
      const currentTable = await researchTableService.getResearchTable(tableId);
      const updatedConfig = {
        ...currentTable.table_config,
        hidden_columns: Array.from(hiddenColumnsSet)
      };
      
      await researchTableService.updateResearchTable(tableId, {
        table_config: updatedConfig
      });
      
      console.log('✅ Saved hidden columns to backend:', Array.from(hiddenColumnsSet));
    } catch (error) {
      console.error('Error saving hidden columns:', error);
      message.error('Failed to save column visibility');
    }
  };

  const saveTableConfig = async (columnsToSave: TableColumn[]) => {
    if (!tableId) return;
    
    try {
      // Get the current table config and update only the columns
      const currentTable = await researchTableService.getResearchTable(tableId);
      const updatedConfig = {
        ...currentTable.table_config,
        columns: columnsToSave
      };
      
      await researchTableService.saveTableConfig(tableId, updatedConfig as any);
    } catch (error) {
      console.error('Error saving table config:', error);
      message.error('Failed to save column order');
    }
  };
  
  // Merge optimistic updates into rows
  const mergedRows = [...rows, ...optimisticCreators].map(row => {
    const optimisticUpdate = optimisticCreatorUpdates[row.creator.id];
    if (optimisticUpdate) {
      return {
        ...row,
        creator: {
          ...row.creator,
          ...optimisticUpdate,
          // Merge contacts specifically
          contacts: optimisticUpdate.contacts || row.creator.contacts
        }
      };
    }
    return row;
  });
  
  // Apply column filters to rows
  const filteredRows = mergedRows.filter(row => {
    // Workflow status filter - only show "in progress" creators
    if (workflowEnabled && showActiveOnly) {
      const status = (tableData[row.id]?.workflow_status?.value as WorkflowStatus) || 'not_started';
      if (status !== 'in_progress') {
        return false;
      }
    }
    
    // Check each column filter
    for (const [columnId, filter] of Object.entries(columnFilters)) {
      const cellData = tableData[row.id]?.[columnId];
      const hasValue = cellData?.value && cellData.value.trim() !== '';
      
      if (filter.type === 'has_value' && !hasValue) {
        return false;
      }
      if (filter.type === 'is_empty' && hasValue) {
        return false;
      }
    }
    return true;
  });
  
  // Check if table has been run (has any cell data with values)
  const hasBeenRun = Object.values(tableData).some(rowData => 
    Object.values(rowData).some(cellData => cellData.value && cellData.value.trim() !== '')
  );
  
  
  const handleImageError = (creatorId: string) => {
    setImageErrors(prev => new Set(prev).add(creatorId));
  };

  const handleEmailUpdate = async (creatorId: string, newEmail: string) => {
    try {
      // Get existing contacts or initialize empty array
      const creator = mergedRows.find(r => r.creator.id === creatorId)?.creator;
      const existingContacts = creator?.contacts || [];
      
      // Find existing email contact
      const emailContactIndex = existingContacts.findIndex((c: any) => c.type === 'email');
      
      let updatedContacts: any[];
      if (newEmail.trim() === '') {
        // Remove email if empty
        updatedContacts = existingContacts.filter((c: any) => c.type !== 'email');
      } else if (emailContactIndex >= 0) {
        // Update existing email
        updatedContacts = [...existingContacts];
        updatedContacts[emailContactIndex] = {
          ...updatedContacts[emailContactIndex],
          value: newEmail
        };
      } else {
        // Add new email
        updatedContacts = [
          ...existingContacts,
          {
            type: 'email' as const,
            value: newEmail,
            role: 'creator' as const,
            status: 'pending' as const
          }
        ];
      }
      
      // Optimistic UI update - update immediately without refresh
      setOptimisticCreatorUpdates(prev => ({
        ...prev,
        [creatorId]: {
          ...prev[creatorId],
          contacts: updatedContacts as any
        }
      }));
      
      // Update backend in background
      await creatorService.updateCreator(creatorId, { contacts: updatedContacts as any });
      
      // Don't clear optimistic update - it will be replaced by real data from refresh/WebSocket
      // This prevents the email from disappearing after editing
      
      message.success('Email updated');
    } catch (error) {
      console.error('Error updating email:', error);
      message.error('Failed to update email');
      // Revert optimistic update on error
      setOptimisticCreatorUpdates(prev => {
        const updated = { ...prev };
        delete updated[creatorId];
        return updated;
      });
    }
  };

  // Auto-search and show suggestions dropdown
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    let mounted = true;
    let currentSearch: Promise<any> | null = null;
    
    const performSearch = async () => {
      const handle = quickAddHandle.trim();
      
      if (!handle || handle.length < 3) {
        if (mounted) {
          setQuickAddVerificationStatus('idle');
          setQuickAddSearchResult(null);
          setQuickAddImageError(false);
          setQuickAddInstagramSuggestions([]);
          setQuickAddTikTokSuggestions([]);
          setShowQuickAddDropdown(false);
          setQuickAddSelectedIndex(-1);
          setIsSearching(false);
          lastSearchedHandleRef.current = '';
        }
        return;
      }
      
      // Don't search if we already have selected a result (found state)
      if (quickAddVerificationStatus === 'found') {
        return;
      }
      
      // Don't re-search the same handle
      if (lastSearchedHandleRef.current === handle) {
        console.log('⏭️ Skipping duplicate search for:', handle);
        return;
      }
      
      // Update ref BEFORE searching to prevent duplicate searches
      lastSearchedHandleRef.current = handle;
      
      console.log('🔍 NEW SEARCH for:', handle, 'on BOTH platforms');
      
      // Show dropdown immediately - use isSearching flag (doesn't block input)
      if (mounted) {
        setShowQuickAddDropdown(true);
        setQuickAddInstagramSuggestions([]); // Clear previous suggestions
        setQuickAddTikTokSuggestions([]);
        setIsSearching(true); // Show loading in dropdown
        setQuickAddVerificationStatus('idle'); // Keep input editable
      }
      
      try {
        // Search BOTH platform APIs in parallel (no library check needed)
        console.log('📡 Searching Instagram + TikTok APIs...');
        
        const [instagramResult, tiktokResult] = await Promise.all([
          platformService.searchInstagramUsers(handle),
          platformService.searchTikTokUsers(handle, 8)
        ]);
        
        let instagramApiResults: any[] = [];
        let tiktokApiResults: any[] = [];
        
        if (instagramResult.success && instagramResult.profiles) {
          instagramApiResults = instagramResult.profiles.map((p: any) => ({
            ...p,
            platform: 'instagram'
          }));
          console.log('✅ Found', instagramApiResults.length, 'Instagram creators');
        }
        
        if (tiktokResult.success && tiktokResult.profiles) {
          tiktokApiResults = tiktokResult.profiles.map((p: any) => ({
            ...p,
            platform: 'tiktok'
          }));
          console.log('✅ Found', tiktokApiResults.length, 'TikTok creators');
        }
        
        if (!mounted) return;
        
        setIsSearching(false); // Search complete
        
        if (instagramApiResults.length === 0 && tiktokApiResults.length === 0) {
          setQuickAddVerificationStatus('not_found');
          setQuickAddInstagramSuggestions([]);
          setQuickAddTikTokSuggestions([]);
          setShowQuickAddDropdown(true);
          return;
        }
        
        // Set platform-specific suggestions
        setQuickAddInstagramSuggestions(instagramApiResults);
        setQuickAddTikTokSuggestions(tiktokApiResults);
        setShowQuickAddDropdown(true);
        setQuickAddVerificationStatus('idle');
        setQuickAddSelectedIndex(0);
        console.log('✅ Showing', instagramApiResults.length, 'Instagram +', tiktokApiResults.length, 'TikTok results');
        
      } catch (error) {
        if (!mounted) return;
        console.error('❌ Error searching platform:', error);
        setIsSearching(false); // Search complete
        setQuickAddVerificationStatus('not_found');
        setQuickAddInstagramSuggestions([]);
        setQuickAddTikTokSuggestions([]);
        setShowQuickAddDropdown(true);
      }
    };
    
    // Only search if:
    // 1. Quick add is open
    // 2. Handle is at least 3 chars
    // 3. Not already in "found" state (user selected a result)
    if (showQuickAdd && quickAddHandle.length >= 3 && quickAddVerificationStatus !== 'found') {
      timeout = setTimeout(performSearch, 100); // Debounce typing (100ms for fast response)
    }
    
    return () => {
      mounted = false;
      clearTimeout(timeout);
    };
  }, [quickAddHandle, showQuickAdd, quickAddVerificationStatus]);
  
  const handleSelectSuggestion = async (profile: any, isExistingCreator = false) => {
    const platform = profile.platform;
    const handle = profile.handle.toLowerCase();
    
    // Show "adding" state
    setQuickAddVerificationStatus('adding');
    setShowQuickAddDropdown(false);
    setQuickAddInstagramSuggestions([]);
    setQuickAddTikTokSuggestions([]);
    setQuickAddSelectedIndex(-1);
    
    try {
      let creatorId: string | null = null;
      
      // Step 1: Create or find creator in library
      console.log(`🔄 [DROPDOWN-STEP-1] Ensuring creator in library...`, { handle, platform });
      
      const creatorData: any = {
        tiktok_data: platform === 'tiktok' ? [profile] : [],
        instagram_data: platform === 'instagram' ? [profile] : [],
        twitch_data: [],
        contacts: []
      };
      
      try {
        const newCreator = await creatorService.createCreator(creatorData);
        if (!newCreator || !newCreator.id) {
          throw new Error('No creator ID returned');
        }
        creatorId = newCreator.id;
        console.log(`✅ [DROPDOWN-STEP-1] Creator in library (id: ${creatorId})`);
      } catch (createError: any) {
        // If duplicate, backend will tell us - that's OK
        const errorMsg = createError.message || createError.toString();
        if (errorMsg.includes('duplicate') || errorMsg.includes('already exists') || errorMsg.includes('unique constraint')) {
          console.log(`ℹ️ [DROPDOWN-STEP-1] Creator already exists, will use existing`);
          // Backend will handle this in Step 2/3 via the profile data
          // We need to fetch to get the ID
          const existingCreators = await creatorService.getCreators();
          const existing = existingCreators.data.find((c: any) => {
            if (platform === 'tiktok') {
              return c.tiktok_data?.some((t: any) => t.handle.toLowerCase() === handle);
            } else {
              return c.instagram_data?.some((i: any) => i.handle.toLowerCase() === handle);
            }
          });
          if (existing) {
            creatorId = existing.id;
            console.log(`✅ [DROPDOWN-STEP-1] Found existing creator (id: ${creatorId})`);
          } else {
            throw new Error('Creator exists but could not be found');
          }
        } else {
          throw createError;
        }
      }
      
      if (!creatorId) {
        throw new Error('Failed to get creator ID');
      }
      
      // Step 2: Add to project (backend handles conflicts + auto-seeds tables)
      if (projectId) {
        try {
          console.log(`🔄 [DROPDOWN-STEP-2] Adding creator to project...`, { creatorId, projectId });
          await creatorService.addCreatorToProject(creatorId, projectId);
          console.log(`✅ [DROPDOWN-STEP-2] Added creator to project`);
        } catch (error: any) {
          const errorMsg = error.message || error.toString();
          if (errorMsg.includes('already') || errorMsg.includes('duplicate') || errorMsg.includes('exists')) {
            console.log(`ℹ️ [DROPDOWN-STEP-2] Creator already in project (OK)`);
          } else {
            console.error('❌ [DROPDOWN-STEP-2] Failed to add to project:', error);
            throw error;
          }
        }
      }
      
      // Step 3: Add to table (backend handles conflicts)
      if (tableId) {
        try {
          console.log(`🔄 [DROPDOWN-STEP-3] Adding creator to table...`, { creatorId, tableId });
          await researchTableService.addCreatorsToTable(tableId, [creatorId]);
          console.log(`✅ [DROPDOWN-STEP-3] Added creator to table`);
        } catch (error: any) {
          const errorMsg = error.message || error.toString();
          if (errorMsg.includes('already') || errorMsg.includes('exists')) {
            console.log(`ℹ️ [DROPDOWN-STEP-3] Creator already in table (OK)`);
          } else {
            console.error('❌ [DROPDOWN-STEP-3] Failed to add to table:', error);
            throw error;
          }
        }
      }
      
      message.success(`Added @${profile.handle}`);
      
      // Reset input
      setQuickAddHandle('');
      setQuickAddSearchResult(null);
      setQuickAddVerificationStatus('idle');
      setQuickAddInstagramSuggestions([]);
      setQuickAddTikTokSuggestions([]);
      lastSearchedHandleRef.current = '';
      setShowQuickAdd(false);
      
      // Add just this creator to the table (no full refresh!)
      if (onCreatorAdded) {
        await onCreatorAdded(creatorId);
      }
      
    } catch (error) {
      console.error('❌ Error adding creator:', error);
      message.error('Failed to add creator');
      setQuickAddVerificationStatus('idle');
      setQuickAddHandle('');
      setShowQuickAdd(false);
    }
  };

  const formatFollowers = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const handleQuickAddCreator = async () => {
    if (!tableId || !quickAddSearchResult) return;
    
    setQuickAddVerificationStatus('adding');
    
    let creatorToAdd: any;
    let creatorWasCreated = false;
    
    try {
      const profile = quickAddSearchResult;
      const handle = profile.handle.toLowerCase();
      const platform = profile.platform || 'instagram';
      
      console.log('🔄 [ADD-CREATOR] Starting add flow:', { handle, platform, tableId, projectId });
      
      // ===== STEP 1: Ensure Creator Exists in Library =====
      const existingCreators = await creatorService.getCreators();
      const existingCreator = existingCreators.data.find((c: any) => {
        if (platform === 'tiktok') {
          return c.tiktok_data?.some((t: any) => t.handle.toLowerCase() === handle);
        } else {
          return c.instagram_data?.some((i: any) => i.handle.toLowerCase() === handle);
        }
      });
      
      if (existingCreator) {
        creatorToAdd = existingCreator;
        console.log(`✅ [STEP 1] Creator exists in library:`, creatorToAdd.id);
      } else {
        const creatorData: any = {
          tiktok_data: platform === 'tiktok' ? [profile] : [],
          instagram_data: platform === 'instagram' ? [profile] : [],
          twitch_data: [],
          contacts: []
        };
        
        try {
          creatorToAdd = await creatorService.createCreator(creatorData);
          creatorWasCreated = true;
          console.log(`✅ [STEP 1] Created new creator:`, creatorToAdd.id);
        } catch (error: any) {
          console.error('❌ [STEP 1] Failed to create creator:', error);
          message.error('Failed to add creator to library');
          setQuickAddVerificationStatus('found');
          return;
        }
      }
      
      // ===== STEP 2: Add Creator to Project (Always Execute) =====
      // Backend handles "already exists" gracefully with ON CONFLICT DO NOTHING
      // This also triggers seeding into ALL project tables automatically
      if (projectId) {
        try {
          console.log(`🔄 [STEP 2] Adding creator to project...`, { creatorId: creatorToAdd.id, projectId });
          await addCreatorToProject(creatorToAdd.id, projectId);
          console.log(`✅ [STEP 2] Added to project (auto-seeded into all project tables)`);
        } catch (error: any) {
          const errorMsg = error.message || error.toString();
          // Silently handle "already in project" - this is OK
          if (errorMsg.includes('already') || errorMsg.includes('duplicate') || errorMsg.includes('exists') || errorMsg.includes('Duplicate')) {
            console.log(`ℹ️ [STEP 2] Creator already in project (OK)`);
          } else {
            console.error('❌ [STEP 2] Failed to add to project:', error);
            // Don't fail completely if creator is at least in library
            message.warning(`Creator added to library, but project sync failed`);
          }
        }
      } else {
        console.warn('⚠️ [STEP 2] No projectId - skipping project assignment');
      }
      
      // ===== STEP 3: Ensure Creator is in THIS Specific Table (Always Execute) =====
      // Even if Step 2 seeded into all tables, we explicitly ensure it's in this one
      // Backend handles "already exists" gracefully with ON CONFLICT DO NOTHING
      try {
        console.log(`🔄 [STEP 3] Adding creator to table...`, { creatorId: creatorToAdd.id, tableId });
        const result = await researchTableService.addCreatorsToTable(tableId, [creatorToAdd.id]);
        console.log(`✅ [STEP 3] Added to table:`, result);
      } catch (error: any) {
        const errorMsg = error.message || error.toString();
        console.error('❌ [STEP 3] Failed to add to table:', error);
        
        // If this fails, something is wrong - show error to user
        if (!errorMsg.includes('already') && !errorMsg.includes('exists')) {
          message.error(`Failed to add creator to table. Please refresh and try again.`);
          setQuickAddVerificationStatus('found');
          return;
        } else {
          console.log(`ℹ️ [STEP 3] Creator already in table (OK)`);
        }
      }
      
      // ===== STEP 4: Add Creator to UI =====
      // Reset UI state
      setQuickAddHandle('');
      setQuickAddSearchResult(null);
      setQuickAddVerificationStatus('idle');
      setQuickAddImageError(false);
      setShowQuickAdd(false);
      
      // Add just this creator to the table (no full refresh!)
      console.log('🔄 [STEP 4] Adding creator to UI...');
      if (onCreatorAdded) {
        await onCreatorAdded(creatorToAdd.id);
        console.log('✅ [STEP 4] Creator added to UI');
      }
      
      // Show success message
      const successMsg = creatorWasCreated 
        ? `@${handle} added!`
        : `@${handle} added to table!`;
      message.success(successMsg);
      
      // Auto-research for columns with auto_update enabled
      const autoUpdateColumns = columns.filter(col => col.auto_update && col.cell_type === 'normal');
      if (autoUpdateColumns.length > 0) {
        // SAFEGUARD: Limit auto-research columns
        const MAX_AUTO_RESEARCH_COLUMNS = 20;
        
        if (autoUpdateColumns.length > MAX_AUTO_RESEARCH_COLUMNS) {
          message.warning(`Auto-research limited to ${MAX_AUTO_RESEARCH_COLUMNS} columns (you have ${autoUpdateColumns.length})`);
          console.warn(`⚠️ [AUTO-UPDATE] Too many auto-update columns (${autoUpdateColumns.length}), limiting to ${MAX_AUTO_RESEARCH_COLUMNS}`);
        } else {
          message.info(`Auto-researching ${autoUpdateColumns.length} columns for @${handle}...`);
        }
        
        console.log(`✨ [AUTO-UPDATE] ${autoUpdateColumns.length} auto-update columns found - researching for new creator`);
        
        const columnsToResearch = autoUpdateColumns.slice(0, MAX_AUTO_RESEARCH_COLUMNS);
        
        // Wait a bit for the row to be loaded after refresh, then trigger research
        setTimeout(() => {
          const creatorRow = rows.find(r => r.creator?.id === creatorToAdd.id);
          if (creatorRow) {
            for (const column of columnsToResearch) {
              try {
                // Use a small delay to avoid overwhelming the API
                const delay = columnsToResearch.indexOf(column) * 100;
                setTimeout(() => {
                  onResearchCell(creatorRow.id, column.id);
                }, delay);
              } catch (error) {
                console.error(`Failed to auto-research ${creatorRow.id} for ${column.name}:`, error);
              }
            }
          } else {
            console.warn(`⚠️ [AUTO-UPDATE] Creator row not found after refresh`);
          }
        }, 1500); // Wait 1.5 seconds for refresh to complete
      }
      
      console.log('✅ [ADD-CREATOR] Flow complete!');
      
    } catch (error) {
      console.error('Unexpected error adding creator:', error);
      
      // If creator was created, show partial success
      if (creatorWasCreated && creatorToAdd) {
        message.warning(`@${quickAddHandle} added to library, but may not be in table yet`);
        setQuickAddVerificationStatus('idle');
      } else {
        message.error('Failed to add creator');
        setQuickAddVerificationStatus('found'); // Revert to found state on error
      }
    }
  };

  const handleCopyClick = async () => {
    if (!onCopyToClipboard) return;
    
    setCopyStatus('copying');
    
    try {
      await onCopyToClipboard();
      setCopyStatus('success');
      
      // Reset to idle after 2 seconds
      setTimeout(() => {
        setCopyStatus('idle');
      }, 2000);
    } catch (error) {
      setCopyStatus('error');
      
      // Reset to idle after 2 seconds
      setTimeout(() => {
        setCopyStatus('idle');
      }, 2000);
    }
  };

  // Handler for Run All Cells button - only runs platform, conversation, and track_posts cells
  const handleRunAllCells = () => {
    const executableCells = columns.filter(col => 
      col.cell_type === 'normal' || col.cell_type === 'communication' || col.cell_type === 'track_posts'
    );
    
    let cellsToRun = 0;
    mergedRows.forEach(row => {
      executableCells.forEach(column => {
        // Skip disabled cells
        if (checkCellDisabled(row.id, column)) {
          return;
        }
        
        // Run the cell
        onResearchCell(row.id, column.id);
        cellsToRun++;
      });
    });
    
    message.success(`Running ${cellsToRun} cells...`);
  };

  // Handler for Run Unrun Cells button - only runs platform, conversation, and track_posts cells that haven't been run
  const handleRunUnrunCells = () => {
    const executableCells = columns.filter(col => 
      col.cell_type === 'normal' || col.cell_type === 'communication' || col.cell_type === 'track_posts'
    );
    
    let cellsToRun = 0;
    mergedRows.forEach(row => {
      executableCells.forEach(column => {
        const cellData = tableData[row.id]?.[column.id];
        const hasValue = cellData?.value && cellData.value.trim() !== '';
        
        // Skip cells that already have values or are disabled
        if (hasValue || checkCellDisabled(row.id, column)) {
          return;
        }
        
        // Run the cell
        onResearchCell(row.id, column.id);
        cellsToRun++;
      });
    });
    
    if (cellsToRun > 0) {
      message.success(`Running ${cellsToRun} unrun cells...`);
    } else {
      message.info('All cells already have data');
    }
  };


  // Helper function to generate template preview key
  const getPreviewKey = (rowId: string, columnId: string) => `${rowId}-${columnId}`;


  // Calculate container height to determine how many placeholder rows to show
  React.useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        const rect = tableContainerRef.current.getBoundingClientRect();
        setContainerHeight(rect.height);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Calculate how many rows can fit in the available space
  const calculatePlaceholderRows = () => {
    if (!containerHeight) return 30; // Higher default fallback
    
    const rowHeight = 38; // min-height from StyledTableRow
    const headerHeight = 36; // approximate header height
    const padding = 20; // total padding (8px top + 12px bottom)
    const availableHeight = containerHeight - headerHeight - padding;
    const maxVisibleRows = Math.floor(availableHeight / rowHeight);
    
    // Fill the entire available space plus extra for scrolling to eliminate white space
    const targetRows = Math.max(maxVisibleRows + 15, 30);
    return Math.max(targetRows - rows.length, 0);
  };




  const startEditing = (rowId: string, columnId: string, currentValue: string) => {
    setEditingCell({ rowId, columnId });
    setEditValue(currentValue || '');
  };

  const saveEdit = () => {
    if (editingCell) {
      // Call the callback to update the cell value
      onUpdateCellValue(editingCell.rowId, editingCell.columnId, editValue);
      console.log('✅ [ResearchTableView] Saving manual edit:', editingCell, editValue);
    }
    setEditingCell(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };





  const renderAvatar = (platformData: {
    platform: CreatorPlatformType | 'unknown';
    handle: string;
    name: string;
    followers_count?: number;
    engagement_rate?: number;
    profile_image_url?: string;
  }, creatorId: string) => {
    const profileImageUrl = platformData.profile_image_url;
    const hasValidUrl = profileImageUrl && typeof profileImageUrl === 'string' && profileImageUrl.trim() !== '';
    const hasProfileImage = hasValidUrl && !imageErrors.has(creatorId);
    
    // Debug logging for profile images
    console.log('🖼️ [ResearchTableView] Avatar render debug:', {
      creatorId,
      platform: platformData.platform,
      handle: platformData.handle,
      profileImageUrl,
      hasValidUrl,
      hasProfileImage,
      imageErrors: imageErrors.has(creatorId)
    });
    
    return (
      <CreatorAvatar $platform={platformData.platform} $hasImage={!!hasProfileImage}>
        {hasProfileImage ? (
          <>
            <ProfileImage
              src={profileImageUrl}
              alt={`${platformData.name} profile`}
              onError={() => handleImageError(creatorId)}
            />
            <AvatarFallback style={{ opacity: 0 }}>
              {platformData.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </>
        ) : (
          <AvatarFallback>
            {platformData.name.charAt(0).toUpperCase()}
          </AvatarFallback>
        )}
      </CreatorAvatar>
    );
  };

  const renderCreatorCell = (creator: Creator, rowId: string) => {
    console.log('🎯 [ResearchTableView] renderCreatorCell called:', { rowId, creatorId: creator?.id });
    
    // Check if this is a skeleton row
    if (rowId.startsWith('skeleton-')) {
      return (
        <CreatorCell>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, padding: '2px' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              borderRadius: '50%', 
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              backgroundSize: '200% 100%',
              animation: 'loading 1.5s infinite'
            }} />
            <CreatorInfo>
              <div style={{ 
                height: '12px', 
                width: '80px',
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'loading 1.5s infinite',
                borderRadius: '4px'
              }} />
            </CreatorInfo>
          </div>
        </CreatorCell>
      );
    }

    // Check if this is a mapping row - show CSV name in left column
    if (rowId.startsWith('mapping-') && creator.creator_shipping?.mapping_data) {
      const mappingData = creator.creator_shipping.mapping_data as any;
      
      // Show the CSV name in the left "Excel Name" column
      return (
        <CreatorCell>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              flex: 1, 
              padding: '2px'
            }}
          >
            <div style={{ 
              width: '24px', 
              height: '24px', 
              borderRadius: '6px', 
              background: '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '600',
              color: '#6b7280'
            }}>
              CSV
            </div>
            <CreatorInfo>
              <CreatorName>{mappingData.csv_name}</CreatorName>
            </CreatorInfo>
          </div>
        </CreatorCell>
      );
    }

    const platformData = getCreatorPrimaryPlatform(creator);
    const allPlatforms = getCreatorPlatforms(creator);
    
    // Debug logging for platform data extraction
    console.log('🔍 [ResearchTableView] Platform data extraction debug:', {
      creatorId: creator.id,
      creatorData: creator,
      extractedPlatformData: platformData,
      allPlatforms: allPlatforms
    });
    
    const isSelected = selectionMode && selectedCreators?.includes(creator.id);
    const contactInfo = getCreatorContactInfo(creator);
    const canOpenDrawer = hasContactMethod(creator);
    
    // Format contact display
    const contactText = contactInfo.email || '';
    
    // Get row's last updated timestamp
    const rowData = tableData[rowId] || {};
    const { timestamp, isOld } = getRowLastRunTime(rowData);
    const hasBeenRun = timestamp !== undefined;
    
    return (
      <CreatorCell>
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: selectionMode ? '8px' : '10px', 
            flex: 1, 
            cursor: selectionMode ? 'default' : 'default',
            padding: '2px',
            borderRadius: '4px',
            transition: 'background-color 0.12s ease'
          }}
          onClick={selectionMode ? undefined : undefined}
          onMouseEnter={selectionMode || !canOpenDrawer ? undefined : (e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={selectionMode || !canOpenDrawer ? undefined : (e) => {
            e.currentTarget.style.backgroundColor = '#fefefe';
          }}
        >
          {selectionMode && (
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onCreatorSelect?.(creator.id, e.target.checked);
              }}
            />
          )}
          {renderAvatar(platformData, creator.id)}
          <CreatorInfo>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CreatorName>@{platformData.name}</CreatorName>
              {/* Platform badges - right after creator name */}
              {allPlatforms.length > 0 && (
                <PlatformBadges style={{ margin: 0 }}>
                  {allPlatforms.map((platform) => (
                    <PlatformBadge 
                      key={platform.platform}
                      href={getPlatformUrl(platform.platform, platform.handle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title={`View ${platform.handle} on ${platform.platform}`}
                    >
                      <PlatformIcon platform={platform.platform} size={6} />
                    </PlatformBadge>
                  ))}
                </PlatformBadges>
              )}
            </div>
            
            {/* Email display logic - underneath creator name */}
            {(() => {
                const emailContact = creator.contacts?.find((c: any) => c.type === 'email');
                const hasEmail = emailContact?.value || creator.creator_shipping?.email;
                const hasReasoning = emailContact?.metadata?.reasoning;
                
                if (hasEmail) {
                  return (
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: '2px',
                      fontSize: '10px',
                      color: '#6b7c93'
                    }}>
                      <MailIcon style={{ width: '10px', height: '10px' }} />
                      <span style={{ 
                        maxWidth: '140px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap'
                      }}>
                        {emailContact?.value || creator.creator_shipping?.email}
                      </span>
                      {hasReasoning && (
                        <Tooltip title="View AI reasoning" placement="top">
                          <InformationCircleIcon 
                            style={{ width: '10px', height: '10px', cursor: 'pointer', color: '#635bff' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const cellData = {
                                value: emailContact.value,
                                reasoning: emailContact.metadata?.reasoning,
                                loading: false,
                                timestamp: new Date().toISOString()
                              };
                              const column = { 
                                name: 'Email', 
                                id: 'email', 
                                data_type: 'text' as const,
                                cell_type: 'normal' as const
                              };
                              onOpenCellSidebar?.(cellData, column, creator);
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  );
                } else {
                  // Show "Add email" button if no email exists (and not currently searching)
                  if (onEditCreator) {
                    return (
                      <div 
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginTop: '2px',
                          fontSize: '10px',
                          color: '#9ca3af',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditCreator(creator);
                        }}
                      >
                        <PlusIcon style={{ width: '10px', height: '10px' }} />
                        <span>Add email</span>
                      </div>
                    );
                  }
                  
                  return null;
                }
              })()}
          </CreatorInfo>
          
          {/* Settings dropdown - three dots */}
          {(onEditCreator || onRemoveCreator) && (
            <div style={{ marginLeft: 'auto', paddingLeft: '8px' }}>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    {
                      key: 'run-row',
                      label: 'Run full row',
                      icon: <PlayIcon style={{ width: '14px', height: '14px' }} />,
                      onClick: (e: any) => {
                        e?.domEvent?.stopPropagation();
                        // Run all executable columns for this creator
                        columns.forEach(column => {
                          const canExecute = canColumnBeExecuted(column);
                          if (canExecute) {
                            // Small delay to avoid overwhelming the API
                            const delay = columns.indexOf(column) * 100;
                            setTimeout(() => {
                              onResearchCell(rowId, column.id);
                            }, delay);
                          }
                        });
                        message.success('Running all columns for creator');
                      }
                    },
                    ...(onEditCreator ? [{
                      key: 'edit',
                      label: 'Edit creator details',
                      icon: <CogIcon style={{ width: '14px', height: '14px' }} />,
                      onClick: (e: any) => {
                        e?.domEvent?.stopPropagation();
                        onEditCreator(creator);
                      }
                    }] : []),
                    ...(onRemoveCreator ? [{
                      key: 'delete',
                      label: 'Remove from project',
                      icon: <TrashIcon style={{ width: '14px', height: '14px' }} />,
                      onClick: (e: any) => {
                        e?.domEvent?.stopPropagation();
                        onRemoveCreator(creator.id);
                      },
                      danger: true
                    }] : [])
                  ],
                  onClick: (e: any) => {
                    e.domEvent.stopPropagation();
                  }
                }}
                placement="bottomRight"
              >
                <ActionButton 
                  onClick={(e) => e.stopPropagation()}
                  style={{ padding: '4px' }}
                >
                  <DotsVerticalIcon style={{ width: '12px', height: '12px', color: '#6b7c93' }} />
                </ActionButton>
              </Dropdown>
            </div>
          )}
          
        </div>
      </CreatorCell>
    );
  };

  const renderContactCell = (creator: Creator, rowId: string) => {
    // Check if this is a skeleton row
    if (rowId.startsWith('skeleton-')) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '100%',
          height: '100%',
          minHeight: '38px'
        }}>
          {/* Skeleton contact icon */}
          <div style={{ 
            width: '14px', 
            height: '14px', 
            borderRadius: '2px', 
            background: 'rgba(0, 0, 0, 0.04)',
            animation: 'loading 1.5s infinite'
          }} />
        </div>
      );
    }

    // Check if this is a mapping row - no actions needed
    if (rowId.startsWith('mapping-')) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '100%',
          height: '100%',
          minHeight: '38px'
        }}>
          {/* No actions for mapping rows */}
        </div>
      );
    }

    const emailStatus = getCreatorEmailStatus(creator, creatorThreadStatus);
    const hasEmail = emailStatus.status !== 'no-email';
    const isClickable = hasEmail && onOpenCreatorConversation;
    const isHovering = hoveredContactCell === creator.id;

    return (
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '100%',
          height: '100%',
          minHeight: '38px',
          cursor: isClickable || (!hasEmail && onEditCreator) ? 'pointer' : 'default'
        }}
        onMouseEnter={() => setHoveredContactCell(creator.id)}
        onMouseLeave={() => setHoveredContactCell(null)}
      >
        {hasEmail ? (
          <MailIcon 
            style={{ 
              width: '14px', 
              height: '14px', 
              color: '#6b7280',
              transition: 'all 0.15s ease',
              opacity: isHovering ? 1 : 0.8,
              transform: isHovering ? 'scale(1.1)' : 'scale(1)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isClickable) {
                onOpenCreatorConversation(creator);
              }
            }}
          />
        ) : (
          <>
            {isHovering ? (
              <PlusIcon 
                style={{ 
                  width: '14px', 
                  height: '14px', 
                  color: '#6b7280',
                  transition: 'all 0.15s ease'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onEditCreator) {
                    onEditCreator(creator);
                  }
                }}
              />
            ) : (
              <MailIcon 
                style={{ 
                  width: '14px', 
                  height: '14px', 
                  color: '#6b7280',
                  opacity: 0.3,
                  transition: 'all 0.15s ease'
                }}
              />
            )}
          </>
        )}
      </div>
    );
  };

  // Helper function to check if a cell is disabled
  const checkCellDisabled = (rowId: string, column: TableColumn): boolean => {
    const cellType = column.cell_type || 'normal';
    const row = mergedRows.find(r => r.id === rowId);
    
    if (!row) return false;
    
    // Check if cell is disabled due to column dependencies
    const isDependencyDisabled = column.depends_on ? (() => {
      const dependencyValue = tableData[rowId]?.[column.depends_on.column_id]?.value;
      return !dependencyValue || !column.depends_on.required_values.includes(dependencyValue);
    })() : false;
    
    // Relationship cells don't need platform checks
    if (cellType === 'relationship') {
      return isDependencyDisabled;
    }
    
    // Check platform compatibility
    const isPlatformDisabled = isPlatformCellDisabled 
      ? isPlatformCellDisabled(row.creator, column) 
      : false;
    
    return isDependencyDisabled || isPlatformDisabled;
  };

  // Helper to get missing requirement message for disabled cells
  const getMissingRequirement = (rowId: string, column: TableColumn): string | null => {
    const row = mergedRows.find(r => r.id === rowId);
    if (!row) return null;
    
    const creator = row.creator;
    const cellType = column.cell_type || 'normal';
    
    // Check email requirement for communication/outreach columns
    if (cellType === 'communication' || cellType === 'outreach') {
      const contactInfo = creator.contacts?.find((c: any) => c.type === 'email')?.value || 
                         creator.creator_shipping?.email;
      
      if (!contactInfo || 
          contactInfo.includes('noemail_') || 
          contactInfo.includes('unknown.com') || 
          contactInfo.includes('@example.com')) {
        return 'Creator is missing a valid email address';
      }
    }
    
    // Check platform requirements for platform-specific columns
    if (column.platform_resources && column.platform_resources.length > 0) {
      const columnRequiresTikTok = column.platform_resources.some((r: any) => r.platform === 'tiktok');
      const columnRequiresInstagram = column.platform_resources.some((r: any) => r.platform === 'instagram');
      
      const creatorHasTikTok = creator.tiktok_data && creator.tiktok_data.length > 0;
      const creatorHasInstagram = creator.instagram_data && creator.instagram_data.length > 0;
      
      // If column requires TikTok but creator doesn't have TikTok data
      if (columnRequiresTikTok && !creatorHasTikTok && !columnRequiresInstagram) {
        return 'Creator is missing TikTok data';
      }
      
      // If column requires Instagram but creator doesn't have Instagram data
      if (columnRequiresInstagram && !creatorHasInstagram && !columnRequiresTikTok) {
        return 'Creator is missing Instagram data';
      }
      
      // If column requires both platforms but creator has neither
      if ((columnRequiresTikTok || columnRequiresInstagram) && !creatorHasTikTok && !creatorHasInstagram) {
        return 'Creator is missing required platform data';
      }
    }
    
    return null;
  };

  const renderDataCell = (rowId: string, column: TableColumn) => {
    // Check if this is a skeleton row
    if (rowId.startsWith('skeleton-')) {
      return (
        <CellContent>
          <div style={{ 
            height: '12px', 
            width: '60%',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'loading 1.5s infinite',
            borderRadius: '4px'
          }} />
        </CellContent>
      );
    }

    // Special handling for mapping rows in the "Creator" column (excel_name)
    if (rowId.startsWith('mapping-') && column.id === 'excel_name') {
      const row = mergedRows.find(r => r.id === rowId);
      const creator = row?.creator;
      const mappingData = creator?.creator_shipping?.mapping_data as any;
      
      if (creator && mappingData?.is_matched) {
        // Show TikTok creator with profile image
        const platformData = getCreatorPrimaryPlatform(creator);
        return (
          <CellContent>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              padding: '2px 0'
            }}>
              {renderAvatar(platformData, creator.id)}
              <div style={{ 
                fontSize: '11px', 
                fontWeight: '500', 
                color: '#222429'
              }}>
                @{platformData.name}
              </div>
            </div>
          </CellContent>
        );
      } else {
        // Show "No match found" text only
        return (
          <CellContent>
            <div style={{ 
              fontSize: '11px', 
              color: '#9ca3af',
              fontStyle: 'italic'
            }}>
              No match found
            </div>
          </CellContent>
        );
      }
    }

    const cellData = tableData[rowId]?.[column.id] || { value: '', loading: false };
    const cellType = column.cell_type || 'normal';
    const isCurrentlyEditing = editingCell?.rowId === rowId && editingCell?.columnId === column.id;
    
    const creator = mergedRows.find(r => r.id === rowId)?.creator;
    
    // Handle relationship columns - display calculated value from AI agent
    if (cellType === 'relationship' && column.relationship_config) {
      const hasValue = cellData.value && cellData.value.trim() !== '';
      
      return (
        <>
          <CellWrapper>
            <CellContent 
              $loading={cellData.loading} 
              $error={!!cellData.error}
            >
              {cellData.loading ? (
                <SkeletonLoader>
                  <SkeletonLine $width="60%" />
                </SkeletonLoader>
              ) : cellData.error ? (
                cellData.error_type === 'quota_exhausted' ? (
                  <div style={{ color: '#ef4444', fontSize: '11px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                    ⚠️ Out of AI tokens
                    <a 
                      href="/settings" 
                      onClick={(e) => { e.stopPropagation(); }} 
                      style={{ color: '#3b82f6', textDecoration: 'underline', fontWeight: 500 }}
                    >
                      Buy More
                    </a>
                  </div>
                ) : cellData.error_type === 'trial_expired' ? (
                  <div style={{ color: '#f59e0b', fontSize: '11px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                    ⏰ Trial expired
                    <a 
                      href="/settings" 
                      onClick={(e) => { e.stopPropagation(); }} 
                      style={{ color: '#3b82f6', textDecoration: 'underline', fontWeight: 500 }}
                    >
                      Subscribe
                    </a>
                  </div>
                ) : (
                  <div style={{ color: '#ef4444', fontSize: '11px' }}>
                    {cellData.error}
                  </div>
                )
              ) : hasValue ? (
                cellData.value
              ) : cellData && !cellData.loading ? (
                <span style={{ opacity: 0.3 }}>-</span>
              ) : (
                ''
              )}
            </CellContent>
          </CellWrapper>
          
          {/* Show hover actions for relationship cells */}
          {!isCurrentlyEditing && (
            <CellHoverActions>
              {/* Info button - show for cells with data (hidden if pending change) */}
              {cellData?.value && !cellData?.pending_change && (
                <Tooltip title="View details">
                  <div 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6b7c93',
                      cursor: 'pointer',
                      opacity: 0.7,
                      padding: '4px',
                      marginRight: '2px',
                      borderRadius: '4px',
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const creator = mergedRows.find(r => r.id === rowId)?.creator;
                      onOpenCellSidebar?.(cellData, column, creator);
                    }}
                  >
                    <InformationCircleIcon style={{ width: '14px', height: '14px' }} />
                  </div>
                </Tooltip>
              )}
            </CellHoverActions>
          )}
        </>
      );
    }
    
    // Handle requirement columns - show copyable content with status
    if (cellType === 'requirement' && column.requirement_config) {
      const { template_ref, copyable, required, placeholder } = column.requirement_config;
      const hasContent = cellData.value && cellData.value.trim() !== '';
      
      return (
        <>
          <CellWrapper>
            <CellContent $loading={cellData.loading} $error={false}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                width: '100%'
              }}>
                {/* Status indicator */}
                <div style={{ 
                  width: '16px', 
                  height: '16px', 
                  flexShrink: 0,
                  color: hasContent ? colors.text.success : colors.text.secondary
                }}>
                  {hasContent ? <CheckIcon /> : <XIcon />}
                </div>
                
                {/* Content */}
                <div style={{ 
                  flex: 1, 
                  minWidth: 0,
                  color: hasContent ? colors.text.primary : colors.text.tertiary,
                  fontStyle: hasContent ? 'normal' : 'italic'
                }}>
                  {hasContent ? cellData.value : (placeholder || 'Not provided')}
                </div>
                
                {/* Copy button */}
                {copyable && hasContent && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (cellData.value) {
                        navigator.clipboard.writeText(cellData.value);
                        // TODO: Show toast notification
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '2px',
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: colors.text.secondary,
                      width: '16px',
                      height: '16px'
                    }}
                    title="Copy to clipboard"
                  >
                    <ClipboardCopyIcon />
                  </button>
                )}
              </div>
            </CellContent>
            
            {/* Hover actions for requirement cells */}
            <CellHoverActions>
              <CellOpenButton 
                onClick={(e) => {
                  e.stopPropagation();
                  startEditing(rowId, column.id, cellData.value || '');
                }}
                disabled={false}
                title="Edit requirement"
              >
                <PencilIcon />
              </CellOpenButton>
            </CellHoverActions>
          </CellWrapper>
        </>
      );
    }
    
    // Communication columns will always attempt to run - let AI handle missing emails gracefully
    
    // Use helper function to check if cell is disabled
    const isCellDisabled = checkCellDisabled(rowId, column);
    
    if (cellData.loading) {
      return (
        <>
          <CellContent $loading={true}>
            <SkeletonLoader>
              <SkeletonLine $width="70%" />
            </SkeletonLoader>
          </CellContent>
        </>
      );
    }

    // Handle outreach columns
    if (cellType === 'outreach' && column.outreach_config) {
      let outreachData: OutreachCellData | undefined;
      try {
        outreachData = cellData.value ? JSON.parse(cellData.value as string) : undefined;
      } catch (e) {
        outreachData = undefined;
      }
      
      return (
        <OutreachCell
          data={outreachData}
          onPreview={() => {
            setOutreachPreviewColumn(column);
            setOutreachPreviewCreator(creator || null);
            setOutreachPreviewRowId(rowId);
            setOutreachPreviewOpen(true);
          }}
        />
      );
    }

    // Handle track_posts columns
    if (cellType === 'track_posts' && column.track_posts_config) {
      let trackPostsData: TrackPostsCellData | undefined;
      try {
        trackPostsData = cellData.value ? JSON.parse(cellData.value as string) : undefined;
      } catch (e) {
        trackPostsData = undefined;
      }
      
      return (
        <>
          <CellWrapper>
            <CellContent 
              $loading={cellData.loading}
              onClick={() => {
                // Open sidebar to view post details/verification
                if (onOpenCellSidebar && creator) {
                  onOpenCellSidebar(cellData, column, creator);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <TrackPostsCell
                data={trackPostsData}
                loading={cellData.loading}
                onSearch={() => {
                  // Individual cell execution disabled - use column execution overlay instead
                  return;
                }}
                onView={() => {
                  // Open sidebar with post verification UI
                  if (onOpenCellSidebar && creator) {
                    onOpenCellSidebar(cellData, column, creator);
                  }
                }}
              />
            </CellContent>
            
            {/* Hover actions for track_posts cells */}
            <CellHoverActions>
              <Tooltip title="View details">
                <div 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7c93',
                    cursor: 'pointer',
                    opacity: 0.7,
                    padding: '4px',
                    marginRight: '2px',
                    borderRadius: '4px',
                    transition: 'background 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (onOpenCellSidebar && creator) {
                      onOpenCellSidebar(cellData, column, creator);
                    }
                  }}
                >
                  <EyeIcon style={{ width: '14px', height: '14px' }} />
                </div>
              </Tooltip>
            </CellHoverActions>
          </CellWrapper>
        </>
      );
    }
    
    // Handle companion recommendation cells
    if (cellType === 'companion_recommendation' && column.companion_config && creator) {
      const hasDraft = cellData.value && cellData.value.trim() !== '';
      const managerId = selectedProject?.companion_data?.id || 'campaign-carl';
      const manager = getManagerById(managerId);
      const companionName = manager?.name.split(' ')[1] || 'Carl';
      const companionColor = manager?.color || '#3b82f6';
      const cellKey = `${rowId}-${column.id}`;
      
      const creatorId = creator.id;
      const hasDraftInState = companionDrafts.has(creatorId);
      const isGenerating = companionGeneratingDrafts.has(creatorId);
      
      return (
        <CompanionCell
          hasDraft={hasDraftInState}
          companionName={companionName}
          companionId={managerId}
          companionColor={companionColor}
          creator={creator}
          onOpenConversation={() => {
            // Open CompanionDraftSidebar
            setCompanionDraftOpen(true);
            setCompanionDraftCreator(creator);
          }}
          tableGoal={companionTableGoal}
          projectId={projectId}
          tableId={tableId}
          columnId="companion"
          isGeneratingDraft={companionGeneratingDrafts.has(creator.id)}
          onGenerateDraft={() => handleGenerateDraft(creator)}
          onDraftGenerated={(draft) => {
            console.log('Draft generated for creator:', creator.id, draft);
            // Store in local state
            setCompanionDrafts(prev => new Map(prev).set(creatorId, draft));
          }}
        />
      );
    }

    const isCodeCell = false || false || false;
    const isOutreachCell = false;
    const isInteractiveCell = true; // All cell types can now open the sidebar for analytics
    
    // Check if this cell is being confirmed for overwrite
    const isConfirmingOverwrite = confirmOverwritePopover?.rowId === rowId && 
                                   confirmOverwritePopover?.columnId === column.id;
    
    const cellContent = (
      <>
        <CellWrapper>
          <CellContent 
            $loading={cellData.loading} 
            $error={!!cellData.error}
            onClick={() => {
              if (isCurrentlyEditing) return;
              // Prevent manual editing for Shopify Data cells
              if (cellType === 'shopify') {
                return;
              }
              
              // Debug logging
              console.log(`[DEBUG] Cell clicked: ${rowId}-${column.id}, isCellDisabled=${isCellDisabled}, isOutreachCell=${isOutreachCell}`);
              
              // Handle disabled cells - just return early now
              if (isCellDisabled && !isOutreachCell) {
                console.log(`[DEBUG] Cell click blocked - cell is disabled`);
                return;
              }
              
              // Start editing for cells with values or manual cells
              startEditing(rowId, column.id, cellData.value || '');
            }}
            style={{
              opacity: (isCellDisabled && !isOutreachCell) ? 0.5 : isCodeCell ? 0.7 : 1,
              cursor: 'text'
            }}
        >
          {isCurrentlyEditing ? (
            <CellInput
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={saveEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveEdit();
                } else if (e.key === 'Escape') {
                  cancelEdit();
                }
              }}
              autoFocus
              placeholder={
                column.data_type === 'currency' ? 'e.g., $100 or 100' :
                column.data_type === 'percentage' ? 'e.g., 85% or 85' :
                column.data_type === 'number' ? 'e.g., 1000' :
                column.data_type === 'boolean' ? 'Yes/No, True/False' :
                'Enter value...'
              }
            />
          ) : (
            cellData.error ? (
              cellData.error_type === 'quota_exhausted' ? (
                <span style={{ color: '#ef4444', fontSize: '11px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                  ⚠️ Out of AI tokens - 
                  <a 
                    href="/settings" 
                    onClick={(e) => { e.stopPropagation(); }} 
                    style={{ color: '#3b82f6', textDecoration: 'underline', fontWeight: 500 }}
                  >
                    Buy More
                  </a>
                </span>
              ) : cellData.error_type === 'trial_expired' ? (
                <span style={{ color: '#f59e0b', fontSize: '11px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                  ⏰ Trial expired - 
                  <a 
                    href="/settings" 
                    onClick={(e) => { e.stopPropagation(); }} 
                    style={{ color: '#3b82f6', textDecoration: 'underline', fontWeight: 500 }}
                  >
                    Subscribe
                  </a>
                </span>
              ) : cellData.error
            ) :
            // Show centered play button for empty cells (except manual, outreach, and disabled)
            !cellData.value && cellType !== 'manual' && cellType !== 'outreach' && cellType !== 'requirement' && !isCellDisabled ? (
              cellData && !cellData.loading ? <span style={{ opacity: 0.3 }}>-</span> : ''
            ) : 
            isOutreachCell ? (
              (() => {
                const creator = mergedRows.find(r => r.id === rowId)?.creator;
                const hasTemplate = false;
                
                // If column has no template configured, show "ADD [Column Name]" 
                if (!hasTemplate) {
                  const addText = column.name === 'Send Brief' ? 'ADD Brief' : 
                                 column.name === 'Send Interest Request' ? 'ADD Interest Request' : 
                                 `ADD ${column.name}`;
                  return (
                    <div style={{ 
                      color: '#9ca3af', 
                      fontSize: '11px',
                      fontWeight: '500'
                    }}>
                      {addText}
                    </div>
                  );
                }
                
                // Column has template, always show "Ready to Send" if no AI status yet
                if (!cellData.value) {
                  // Always show "Ready to Send" when template exists but no AI tracking status
                  const colorScheme = getLabelColorScheme('Ready to Send', ['Ready to Send', 'Sent']);
                  return (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: '1px 4px',
                        background: `${colorScheme.color}08`,
                        border: `1px solid ${colorScheme.color}20`,
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 400,
                        color: colorScheme.color,
                        opacity: 0.85
                      }}
                    >
                      <span style={{ fontSize: '6px', opacity: 0.7 }}>●</span>
                      Ready to Send
                    </span>
                  );
                }
                
                // Has AI tracking status, show with label styling
                if (column.data_type === 'labels' && column.label_options) {
                  const colorScheme = getLabelColorScheme(cellData.value, column.label_options);
                  return (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: '1px 4px',
                        background: `${colorScheme.color}08`,
                        border: `1px solid ${colorScheme.color}20`,
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 400,
                        color: colorScheme.color,
                        opacity: 0.85
                      }}
                    >
                      <span style={{ fontSize: '6px', opacity: 0.7 }}>●</span>
                      {cellData.pending_change?.value || cellData.value}
                    </span>
                  );
                }
                
                // Fallback for outreach cells with value but no label styling
                return cellData.pending_change?.value || cellData.value;
              })()
            ) : isCellDisabled ? (
              // Just show empty - the lock icon will show in hover actions
              <span style={{ opacity: 0.3 }}>-</span>
            ) : (
              column.data_type === 'labels' && cellData.value && column.label_options ? (
                // Render subtle colored label for labels data type
                (() => {
                  const colorScheme = getLabelColorScheme(cellData.value, column.label_options);
                  return (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: '1px 4px',
                        background: `${colorScheme.color}08`, // Very subtle background
                        border: `1px solid ${colorScheme.color}20`, // Subtle border
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 400,
                        color: colorScheme.color,
                        opacity: 0.85
                      }}
                    >
                      <span style={{ fontSize: '6px', opacity: 0.7 }}>●</span>
                      {cellData.pending_change?.value || cellData.value}
                    </span>
                  );
                })()
              ) : (
                cellData.pending_change?.value || cellData.value || (isCodeCell ? 'Generated' : '')
              )
            )
          )}
        </CellContent>
        
        </CellWrapper>
        
        {/* Show hover actions for cells */}
        {!isCurrentlyEditing && (
          <CellHoverActions style={{ opacity: (isCellDisabled || cellData?.pending_change) ? 1 : undefined }}>
            {/* Lock icon for disabled cells - always visible */}
            {isCellDisabled && (
              <Tooltip title={getMissingRequirement(rowId, column) || 'Cannot execute this cell'}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9ca3af',
                  cursor: 'default',
                  opacity: 0.5,
                  padding: '4px'
                }}>
                  <LockClosedIcon style={{ width: '14px', height: '14px' }} />
                </div>
              </Tooltip>
            )}
            
            {/* Pending change warning icon - always visible */}
            {!isCellDisabled && cellData?.pending_change && (
              <PendingChangePopover
                cellData={cellData}
                onAccept={() => onAcceptPendingChange?.(rowId, column.id)}
                onDismiss={() => onDismissPendingChange?.(rowId, column.id)}
              >
                <div 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7c93',
                    cursor: 'pointer',
                    opacity: 0.7,
                    padding: '4px',
                    marginRight: '2px',
                    borderRadius: '4px',
                    transition: 'background 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <ExclamationIcon style={{ width: '14px', height: '14px' }} />
                </div>
              </PendingChangePopover>
            )}
            
            {/* Delete button for Shopify discount codes */}
            {!isCellDisabled && cellType === 'shopify' && column.shopify_config?.subtype === 'discount_code' && cellData?.value && onDeleteShopifyDiscountCode && (
              <Tooltip title="Delete discount code">
                <CellOpenButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteShopifyDiscountCode(rowId, column.id);
                  }}
                  style={{
                    opacity: 0.7,
                    cursor: 'pointer'
                  }}
                >
                  <TrashIcon />
                </CellOpenButton>
              </Tooltip>
            )}
            
            {/* Copy/Info button - show for cells with data */}
            {!isCellDisabled && cellData?.value && (
              cellType === 'manual' ? (
                <Tooltip title="Copy value">
                  <CellOpenButton onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(cellData.value);
                  }}>
                    <ClipboardCopyIcon />
                  </CellOpenButton>
                </Tooltip>
              ) : (cellData?.reasoning || cellData?.source) && !cellData?.pending_change && (
                <Tooltip title="View details">
                  <div 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6b7c93',
                      cursor: 'pointer',
                      opacity: 0.7,
                      padding: '4px',
                      marginRight: '2px',
                      borderRadius: '4px',
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const creator = mergedRows.find(r => r.id === rowId)?.creator;
                      onOpenCellSidebar?.(cellData, column, creator);
                    }}
                  >
                    <InformationCircleIcon style={{ width: '14px', height: '14px' }} />
                  </div>
                </Tooltip>
              )
            )}
          </CellHoverActions>
        )}
      </>
    );

    // Wrap cell with confirmation popover if user is overwriting AI value
    if (isConfirmingOverwrite && confirmOverwritePopover) {
      return (
        <PendingChangePopover
          mode="user-overwrite"
          currentValue={confirmOverwritePopover.currentValue}
          newValue={confirmOverwritePopover.newValue}
          aiReasoning={confirmOverwritePopover.currentReasoning}
          onAccept={() => onConfirmOverwrite?.()}
          onDismiss={() => onCancelOverwrite?.()}
          open={true}
          onOpenChange={(open) => {
            if (!open) onCancelOverwrite?.();
          }}
        >
          <div style={{ display: 'inline-block', width: '100%' }}>
            {cellContent}
          </div>
        </PendingChangePopover>
      );
    }

    return cellContent;
  };

  // Always show the actual table structure like Excel

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Workflow Analysis Queue Status */}
      {queueStatus && (queueStatus.isProcessing || queueStatus.queueLength > 0) && (
        <div style={{
          padding: '8px 16px',
          backgroundColor: queueStatus.isProcessing ? '#e6f7ff' : '#f6ffed',
          borderBottom: '1px solid #d9d9d9',
          fontSize: '12px',
          color: '#666'
        }}>
          {queueStatus.isProcessing ? (
            <span>🔄 Processing workflow analysis... ({queueStatus.queueLength} remaining)</span>
          ) : (
            <span>⏳ {queueStatus.queueLength} workflow analyses queued</span>
          )}
          {queueStatus.completedCount > 0 && (
            <span style={{ marginLeft: '16px' }}>
              ✅ {queueStatus.completedCount} completed
            </span>
          )}
          {queueStatus.errorCount > 0 && (
            <span style={{ marginLeft: '16px', color: '#ff4d4f' }}>
              ❌ {queueStatus.errorCount} errors
            </span>
          )}
        </div>
      )}
      
      <TableContainer ref={tableContainerRef} $librarySidebarOpen={librarySidebarOpen} $sidebarCollapsed={sidebarCollapsed}>
      <MainContainer>
        <TableWrapper 
          $drawerOpen={outreachDrawerOpen}
          $librarySidebarOpen={librarySidebarOpen}
          $sidebarCollapsed={sidebarCollapsed}
          $mainSidebarExpanded={mainSidebarExpanded}
        >
          {/* Alert: No creators in table */}
          {!uploadMode && initialLoadComplete && rows.length === 0 && showNoCreatorsAlert && (
            <InfoAlert>
              <InfoAlertIcon>
                <InformationCircleIcon />
              </InfoAlertIcon>
              <InfoAlertContent>
                <InfoAlertTitle>Add creators to get started</InfoAlertTitle>
                <InfoAlertMessage>
                  Open the creator library, drag creators here, or add them inline below
                </InfoAlertMessage>
              </InfoAlertContent>
              <AlertCloseButton onClick={() => setShowNoCreatorsAlert(false)}>
                <XIcon />
              </AlertCloseButton>
            </InfoAlert>
          )}
          
          {/* Alert: Table hasn't been run yet */}
          {!uploadMode && initialLoadComplete && rows.length > 0 && !hasBeenRun && showNotRunAlert && (
            <InfoAlert>
              <InfoAlertIcon>
                <InformationCircleIcon />
              </InfoAlertIcon>
              <InfoAlertContent>
                <InfoAlertTitle>Ready to run research</InfoAlertTitle>
                <InfoAlertMessage>
                  Click the play buttons for empty cells or all cells to start AI research for your creators
                </InfoAlertMessage>
              </InfoAlertContent>
              <AlertCloseButton onClick={() => setShowNotRunAlert(false)}>
                <XIcon />
              </AlertCloseButton>
            </InfoAlert>
          )}
          
          <TableContentWrapper>
            {/* Table Action Bar - fixed at top, not scrolling */}
            {!uploadMode && (
              <div style={{ margin: '0 12px' }}>
                <TableActionBar
                  tableName={tableName}
                  onRunAllEmpty={onRunAllEmpty}
                  onRefreshAll={onRefreshTable}
                  onManageTemplate={onOpenTemplates}
                  onRefresh={onRefreshTable}
                  onExport={onExportCSV}
                  
                  companionEnabled={companionEnabled}
                  companionSettingsButton={(() => {
                    const managerId = selectedProject?.companion_data?.id || 'campaign-carl';
                    const manager = getManagerById(managerId);
                    const companionName = manager?.name || 'Campaign Carl';
                    const companionColor = manager?.color || '#3b82f6';
                    
                    return (
                      <CompanionSettingsPopover
                        companionName={companionName}
                        companionId={managerId}
                        companionColor={companionColor}
                        enabled={companionEnabled}
                        tableGoal={companionTableGoal}
                        onEnabledChange={handleCompanionEnabledChange}
                        onSave={handleCompanionSave}
                        open={companionSettingsOpen}
                        onOpenChange={setCompanionSettingsOpen}
                      >
                        <button
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 10px',
                            background: companionEnabled ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                            color: companionEnabled ? '#3b82f6' : '#6b7280',
                            border: `1px solid ${companionEnabled ? '#3b82f6' : 'rgba(0, 0, 0, 0.08)'}`,
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.12s ease'
                          }}
                        >
                          <SparklesIcon style={{ width: '14px', height: '14px' }} />
                          {companionEnabled ? `${companionName.split(' ')[1]} drafting responses` : `Ask ${companionName.split(' ')[1]} to draft responses`}
                        </button>
                      </CompanionSettingsPopover>
                    );
                  })()}
                     
                  companionSkills={{
                    draftResponses: true,  // This table allows drafting
                    updateCells: true,     // Always on
                    generateCodes: false   // Not enabled for this table
                  }}
                  onDraftResponses={() => console.log('Draft responses clicked')}
                  onUpdateCells={() => console.log('Update cells clicked')}
                  
                  // Workflow props
                  workflowEnabled={workflowEnabled}
                  showActiveOnly={showActiveOnly}
                  onWorkflowFilterChange={handleWorkflowFilterChange}
                  
                  // Column visibility props
                  hiddenColumns={hiddenColumns}
                  allColumns={columns}
                  onShowColumn={handleShowColumn}
                  onHideColumn={handleHideColumn}
                  onShowAllColumns={handleShowAllColumns}
                  onHiddenColumnsChange={(newHiddenColumns) => {
                    console.log('🔄 [ResearchTableView] onHiddenColumnsChange called with:', Array.from(newHiddenColumns));
                    console.log('🔄 [ResearchTableView] props.onHiddenColumnsSave exists?', !!props.onHiddenColumnsSave);
                    if (props.onHiddenColumnsSave) {
                      console.log('🔄 [ResearchTableView] Calling props.onHiddenColumnsSave');
                      props.onHiddenColumnsSave(newHiddenColumns);
                    } else {
                      console.log('🔄 [ResearchTableView] No onHiddenColumnsSave prop, just updating local state');
                      setHiddenColumns(newHiddenColumns);
                    }
                  }}
                  
                  // Column ordering props
                  onReorderColumns={onReorderColumns}
                />
              </div>
            )}
            
            <TableAndSidebarWrapper>
            <CustomTable $sidebarOpen={cellSidebarOpen}>
              <ScrollableTableContainer ref={scrollContainerRef}>
              {/* Template Column Overlays */}
              {columns.map((column, columnIndex) => {
                if (false) {
                  // Calculate offset by summing up widths of all previous columns
                  let columnOffset = 260; // Creator column width
                  for (let i = 0; i < columnIndex; i++) {
                    columnOffset += 180;
                  }
                  return (
                    <div
                      key={`overlay-${column.id}`}
                      style={{
                        position: 'absolute',
                        left: columnOffset,
                        top: 60, // After header
                        width: 160,
                        height: 'calc(100% - 60px)',
                        background: 'rgba(248, 249, 250, 0.4)',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'all'
                      }}
                    >
                      <TemplateConfigCTA
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onEditColumn) {
                            onEditColumn(column);
                          }
                        }}
                      >
{column.name === 'Send Brief' ? 'Add Brief' : column.name === 'Send Interest Request' ? 'Add Interest Message' : 'Add Message'}
                      </TemplateConfigCTA>
                    </div>
                  );
                }
                return null;
              })}

              {/* Table Header */}
              <TableHeader>
                {/* Status Column - Fixed left */}
                <HeaderCell $width={50} $isFirst>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <ClockIcon style={{ width: '14px', height: '14px' }} />
                  </div>
                </HeaderCell>
                
                <HeaderCell $width={250} $isCreator>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span>Creator</span>
                  </div>
                </HeaderCell>
                
                {/* Email Sources Column - Fixed right of creator */}
                <HeaderCell $width={50}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <MailIcon style={{ width: '14px', height: '14px' }} />
                  </div>
                </HeaderCell>
                
                {/* Workflow Status Column - appears after creator when workflow is enabled */}
                {workflowEnabled && (
                  <HeaderCell $width={140}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>Status</span>
                    </div>
                  </HeaderCell>
                )}
                
                {/* Actual columns */}
                {visibleColumns.map((column, index) => {
                  const columnWidth = getColumnWidth(column.id);
                  const columnHasBeenRun = hasColumnBeenRun(column.id);
                  const canExecuteColumn = canColumnBeExecuted(column);
                  
                  return (
                  <HeaderCell 
                    key={column.id} 
                    $width={columnWidth} 
                    $isOutreach={false}
                    onMouseEnter={() => setHoveredColumn(column.id)}
                    onMouseLeave={() => setHoveredColumn(null)}
                  >
                    <div style={{ 
                      flex: 1, 
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 0,
                      gap: '6px'
                    }}>
                      {/* Play icon for unexecuted columns, Filter icon for executed columns */}
                      {canExecuteColumn && (
                        columnHasBeenRun ? (
                          <Dropdown
                            trigger={['click']}
                            menu={{
                              items: [
                                {
                                  key: 'has_value',
                                  label: 'Has value',
                                  onClick: (e: any) => {
                                    e?.domEvent?.stopPropagation();
                                    setColumnFilters(prev => ({
                                      ...prev,
                                      [column.id]: { type: 'has_value' }
                                    }));
                                    message.success('Showing rows with values');
                                  }
                                },
                                {
                                  key: 'is_empty',
                                  label: 'Is empty',
                                  onClick: (e: any) => {
                                    e?.domEvent?.stopPropagation();
                                    setColumnFilters(prev => ({
                                      ...prev,
                                      [column.id]: { type: 'is_empty' }
                                    }));
                                    message.success('Showing rows without values');
                                  }
                                },
                                {
                                  key: 'clear',
                                  label: 'Clear filter',
                                  onClick: (e: any) => {
                                    e?.domEvent?.stopPropagation();
                                    setColumnFilters(prev => {
                                      const newFilters = { ...prev };
                                      delete newFilters[column.id];
                                      return newFilters;
                                    });
                                    message.success('Filter cleared');
                                  },
                                  disabled: !columnFilters[column.id]
                                }
                              ]
                            }}
                          >
                            <FilterIcon 
                              onClick={(e) => e.stopPropagation()}
                              style={{ 
                                width: '14px', 
                                height: '14px', 
                                color: columnFilters[column.id] ? colors.palette.indigo[500] : colors.text.secondary,
                                cursor: 'pointer',
                                flexShrink: 0,
                                transition: 'all 0.15s ease',
                                opacity: columnFilters[column.id] ? 1 : 0.6
                              }}
                            />
                          </Dropdown>
                        ) : (
                          <Tooltip title="Run column for all creators">
                            <PlayIcon 
                              onClick={(e) => {
                                e.stopPropagation();
                                onResearchAllInColumn(column.id);
                              }}
                              style={{ 
                                width: '14px', 
                                height: '14px', 
                                color: colors.text.primary,
                                cursor: 'pointer',
                                flexShrink: 0,
                                transition: 'opacity 0.15s ease'
                              }}
                            />
                          </Tooltip>
                        )
                      )}
                      
                      {editingColumnName === column.id ? (
                        <input
                          autoFocus
                          type="text"
                          value={editingColumnNameValue}
                          onChange={(e) => setEditingColumnNameValue(e.target.value)}
                          onBlur={() => handleSaveColumnName(column)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveColumnName(column);
                            } else if (e.key === 'Escape') {
                              handleCancelEditColumnName();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            border: '1px solid #3b82f6',
                            borderRadius: '3px',
                            padding: '2px 6px',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: colors.text.primary,
                            background: '#fff',
                            outline: 'none'
                          }}
                        />
                      ) : (
                        <span 
                          onClick={() => handleStartEditColumnName(column)}
                          style={{
                            textOverflow: 'ellipsis', 
                            overflow: 'hidden', 
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                            cursor: 'text'
                          }}
                          title="Click to edit column name"
                        >
                          {column.cell_type === 'companion_recommendation' 
                            ? `Ask ${selectedProject?.companion_data?.id ? (getManagerById(selectedProject.companion_data.id)?.name.split(' ')[1] || 'Carl') : 'Carl'}`
                            : column.name
                          }
                        </span>
                      )}
                    </div>
                    
                    {/* Resize handle */}
                    <ResizeHandle
                      onMouseDown={(e) => handleResizeStart(column.id, e)}
                    />
                    
                    {/* Platform icons for normal columns */}
                    {column.cell_type === 'normal' && column.platform_resources && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        {column.platform_resources.map((resource, idx) => {
                          // Add defensive checks for resource and platform property
                          if (!resource || !resource.platform || typeof resource.platform !== 'string') {
                            console.warn('Invalid platform resource:', resource);
                            return null;
                          }
                          
                          return (
                            <div key={idx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '16px',
                              height: '16px',
                              background: 'rgba(0, 0, 0, 0.03)',
                              borderRadius: '3px'
                            }}>
                              <PlatformIcon 
                                platform={resource.platform} 
                                size={11}
                              />
                            </div>
                          );
                        }).filter(Boolean)}
                      </div>
                    )}
                    
                    {/* Platform icon for columns */}
                    {column.platform_icon && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        background: 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '3px'
                      }}>
                        <PlatformIcon 
                          platform={column.platform_icon} 
                          size={11}
                        />
                      </div>
                    )}
                    
                    {/* Email provider icon for communication and normal columns with email sources */}
                    {column.email_provider && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        background: 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '3px'
                      }}>
                        {column.email_provider === 'outlook' ? (
                          <OutlookIcon size={11} />
                        ) : (
                          <GmailIcon size={11} />
                        )}
                      </div>
                    )}
                    
                    {/* Calculator icon for relationship columns */}
                    {column.cell_type === 'relationship' && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        background: 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '3px'
                      }}>
                        <CalculatorIcon style={{ width: '11px', height: '11px', color: colors.text.secondary }} />
                      </div>
                    )}

                    {/* Send icon for outreach columns */}
                    {column.cell_type === 'outreach' && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        background: 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '3px'
                      }}>
                        <PaperAirplaneIcon style={{ width: '11px', height: '11px', color: colors.text.secondary, transform: 'rotate(90deg)' }} />
                      </div>
                    )}
                    
                    {/* Shopify icon for shopify columns */}
                    {column.cell_type === 'shopify' && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        background: 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '3px'
                      }}>
                        <ShopifyIcon size={11} />
                      </div>
                    )}
                    
                    {/* Companion avatar for companion recommendation columns */}
                    {column.cell_type === 'companion_recommendation' && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        background: 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <BoringAvatar
                          size={11}
                          name="campaign-carl"
                          variant="beam"
                          colors={['#3b82f6', '#3b82f6dd', '#3b82f6bb', '#3b82f699', '#f3f4f6']}
                        />
                      </div>
                    )}
                    
                    <div style={{ 
                      display: 'flex', 
                      gap: '2px', 
                      marginLeft: '4px',
                      opacity: 0.6
                    }}
                    className="header-actions"
                    >
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: [
                            {
                              key: 'edit',
                              label: 'Edit column',
                              icon: <PencilIcon style={{ width: '14px', height: '14px' }} />,
                              onClick: (e: any) => {
                                e?.domEvent?.stopPropagation();
                                const target = e.domEvent.target as HTMLElement;
                                const rect = target.getBoundingClientRect();
                                setColumnConfigPopover({ 
                                  column, 
                                  anchorEl: target,
                                  position: { top: rect.top, left: rect.left }
                                });
                              }
                            },
                            ...(canColumnBeExecuted(column) ? [{
                              key: 'rerun',
                              label: 'Re-run column',
                              icon: <PlayIcon style={{ width: '14px', height: '14px' }} />,
                              onClick: (e: any) => {
                                e?.domEvent?.stopPropagation();
                                onResearchAllInColumn(column.id);
                              }
                            }] : []),
                            {
                              key: 'delete',
                              label: 'Delete column',
                              icon: <TrashIcon style={{ width: '14px', height: '14px' }} />,
                              onClick: (e: any) => {
                                e?.domEvent?.stopPropagation();
                                onDeleteColumn(column.id);
                              },
                              danger: true
                            }
                          ]
                        }}
                      >
                        <ActionButton 
                          onClick={(e) => e.stopPropagation()}
                          style={{ padding: '3px' }}
                        >
                          <DotsVerticalIcon style={{ width: '12px', height: '12px', color: '#6b7c93' }} />
                        </ActionButton>
                      </Dropdown>
                  </div>
                </HeaderCell>
                  );
                })}
              
              {/* Add Column Button - Hide in upload mode */}
              {!uploadMode && (
              <HeaderCell $width={180} $isLast>
                <AddColumnContainer onClick={(e) => {
                  const target = e.currentTarget;
                  const rect = target.getBoundingClientRect();
                  setColumnConfigPopover({ 
                    column: null, 
                    anchorEl: target,
                    position: { top: rect.top, left: rect.left }
                  });
                }}>
                  <AddColumnContent>
                    <PlusIcon />
                    Add Column
                  </AddColumnContent>
                </AddColumnContainer>
              </HeaderCell>
              )}
            </TableHeader>

            {/* Table Body */}
            <TableBody data-table-body>
              
              {/* Actual data rows + optimistic creators + optimistic updates */}
              {filteredRows.map(row => (
                <StyledTableRow key={row.id}>
                  {/* Status Column - Fixed left */}
                  <TableCell $width={50} $isFirst>
                    {(() => {
                      const rowData = tableData[row.id] || {};
                      const { timestamp, isOld } = getRowLastRunTime(rowData);
                      const hasBeenRun = timestamp !== undefined;
                      
                      // Simple styling: show time text with subtle colors
                      const tooltipText = hasBeenRun ? `Updated ${formatTimeAgo(timestamp)}` : 'No data yet';
                      const timeText = hasBeenRun ? formatTimeAgoCompact(timestamp) : '-';
                      
                      return (
                        <Tooltip title={tooltipText}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: '100%',
                            cursor: 'default',
                            fontSize: '11px',
                            fontWeight: 400,
                            color: hasBeenRun ? (isOld ? '#9ca3af' : '#6b7280') : '#d1d5db',
                            transition: 'color 0.12s ease'
                          }}>
                            {timeText}
                          </div>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  
                  <TableCell $width={250} $isCreator>
                    {renderCreatorCell(row.creator, row.id)}
                  </TableCell>
                  
                  {/* Email Sources Column Cell - Fixed right of creator */}
                  <TableCell $width={50}>
                    <EmailSourcesColumn
                      creatorId={row.id}
                      projectId={projectId}
                      onThreadExclusionChange={(creatorId: string, threadId: string, excluded: boolean) => {
                        console.log('Thread exclusion changed:', { creatorId, threadId, excluded });
                        // Handle thread exclusion change if needed
                      }}
                    />
                  </TableCell>
                  
                  {/* Workflow Status Column Cell - appears after creator when workflow is enabled */}
                  {workflowEnabled && (
                    <TableCell $width={140}>
                      <CellWrapper>
                        <WorkflowStatusColumn
                          creatorId={row.id}
                          currentStatus={(tableData[row.id]?.workflow_status?.value as WorkflowStatus) || 'not_started'}
                          pendingStatus={tableData[row.id]?.workflow_status?.pending_change as any}
                          onStatusChange={handleStatusChange}
                          onAcceptPending={onAcceptPendingChange ? (creatorId) => onAcceptPendingChange(creatorId, 'workflow_status') : undefined}
                          onDismissPending={onDismissPendingChange ? (creatorId) => onDismissPendingChange(creatorId, 'workflow_status') : undefined}
                          enableAISuggestions={true}
                          aiSuggestion={aiSuggestions[row.id]}
                          onRequestAISuggestion={handleRequestAISuggestion}
                        />
                        
                        {/* Pending change warning icon - positioned absolutely in bottom right */}
                        {tableData[row.id]?.workflow_status?.pending_change && (
                          <PendingChangePopover
                            cellData={tableData[row.id]?.workflow_status}
                            onAccept={() => onAcceptPendingChange?.(row.id, 'workflow_status')}
                            onDismiss={() => onDismissPendingChange?.(row.id, 'workflow_status')}
                          >
                            <div 
                              style={{
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#6b7c93',
                                cursor: 'pointer',
                                opacity: 0.7,
                                padding: '2px',
                                borderRadius: '4px',
                                transition: 'background 0.15s ease',
                                zIndex: 10
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f5f5f5';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <ExclamationIcon style={{ width: '12px', height: '12px' }} />
                            </div>
                          </PendingChangePopover>
                        )}
                      </CellWrapper>
                    </TableCell>
                  )}
                  
                  {/* Actual column cells */}
                  {visibleColumns.map(column => (
                    <TableCell 
                      key={column.id} 
                      data-column-id={column.id}
                      $width={getColumnWidth(column.id)} 
                      $isOutreach={false}
                      $isDisabled={checkCellDisabled(row.id, column)}
                    >
                      {renderDataCell(row.id, column)}
                    </TableCell>
                  ))}
                  <TableCell $width={180} $isLast>
                    {/* Empty cell to maintain table structure */}
                  </TableCell>
                </StyledTableRow>
              ))}
              
              {/* Empty grid rows to show Excel-like structure */}
              {Array.from({ length: calculatePlaceholderRows() }).map((_, index) => (
                <StyledTableRow key={`empty-row-${index}`} $hasDropdown={index === 0 && showQuickAddDropdown}>
                  {/* Status Column - Fixed left */}
                  <TableCell $width={50} $isFirst>
                    {/* Empty */}
                  </TableCell>
                  <TableCell $width={250} $isCreator style={{ overflow: showQuickAdd && index === 0 ? 'visible' : 'hidden' }}>
                    {/* Show Add Creator for first empty row when available */}
                    {index === 0 && onAddCreatorManually && columns.length > 0 ? (
                      showQuickAdd ? (
                        <QuickAddContainer>
                          <AtSymbol>@</AtSymbol>
                          <QuickAddInput
                            $status={quickAddVerificationStatus}
                            type="text"
                            placeholder="search creator (e.g. mrbeast)"
                            value={quickAddHandle}
                            onChange={(e) => {
                              const value = e.target.value.replace('@', '');
                              setQuickAddHandle(value);
                              if (!value) {
                                setQuickAddVerificationStatus('idle');
                                setQuickAddSearchResult(null);
                                setQuickAddImageError(false);
                                setQuickAddInstagramSuggestions([]);
                                setQuickAddTikTokSuggestions([]);
                                setShowQuickAddDropdown(false);
                                setIsSearching(false);
                                lastSearchedHandleRef.current = '';
                              } else if (quickAddVerificationStatus === 'found') {
                                // User is typing after selecting - clear selection and allow new search
                                setQuickAddSearchResult(null);
                                setQuickAddVerificationStatus('idle');
                                setIsSearching(false);
                                // Keep dropdown open, it will show searching state
                                // Clear last searched so new search can happen
                                lastSearchedHandleRef.current = '';
                              }
                              // Input is always editable - search happens in background
                            }}
                            onKeyDown={(e) => {
                              // Always allow Escape to close dropdown
                              if (e.key === 'Escape' && showQuickAddDropdown) {
                                e.preventDefault();
                                setShowQuickAddDropdown(false);
                              }
                              // Note: Keyboard navigation removed for two-column layout
                              // Users can click on desired platform results
                            }}
                            onBlur={() => {
                              // Close on blur (click outside) - only if not found/adding
                              if (quickAddVerificationStatus !== 'found' && quickAddVerificationStatus !== 'adding') {
                                setTimeout(() => {
                                  setShowQuickAdd(false);
                                  setQuickAddHandle('');
                                  setQuickAddVerificationStatus('idle');
                                  setQuickAddSearchResult(null);
                                  setQuickAddImageError(false);
                                  setQuickAddInstagramSuggestions([]);
                                  setQuickAddTikTokSuggestions([]);
                                  setShowQuickAddDropdown(false);
                                }, 200);
                              }
                            }}
                            autoFocus
                            disabled={quickAddVerificationStatus === 'searching' || quickAddVerificationStatus === 'adding'}
                          />
                          {/* Show status icon only when searching, adding, or not found */}
                          {(quickAddVerificationStatus === 'searching' || quickAddVerificationStatus === 'adding' || quickAddVerificationStatus === 'not_found') && (
                            <QuickAddStatusIcon $status={quickAddVerificationStatus}>
                              {quickAddVerificationStatus === 'searching' && <LoadingOutlined />}
                              {quickAddVerificationStatus === 'adding' && <LoadingOutlined />}
                              {quickAddVerificationStatus === 'not_found' && <ExclamationCircleOutlined />}
                            </QuickAddStatusIcon>
                          )}
                          
                          {/* Show profile image when found or adding */}
                          {(quickAddVerificationStatus === 'found' || quickAddVerificationStatus === 'adding') && quickAddSearchResult && (
                            <QuickAddProfileImageWrapper>
                              {quickAddSearchResult.profile_image_url && !quickAddImageError ? (
                                <QuickAddProfileImage 
                                  src={quickAddSearchResult.profile_image_url} 
                                  alt={quickAddSearchResult.handle}
                                  onError={() => setQuickAddImageError(true)}
                                />
                              ) : (
                                <QuickAddProfileFallback $platform={quickAddSearchResult.platform || 'instagram'}>
                                  {quickAddSearchResult.handle?.charAt(0)?.toUpperCase() || '?'}
                                </QuickAddProfileFallback>
                              )}
                            </QuickAddProfileImageWrapper>
                          )}
                          
                          {/* Suggestions Dropdown */}
                          {showQuickAddDropdown && (
                            <SuggestionsDropdown>
                              {isSearching ? (
                                <TwoColumnGrid>
                                  <PlatformColumn>
                                    <PlatformColumnHeader $platform="instagram">
                                      <PlatformIcon platform="instagram" size={11} />
                                      Results
                                    </PlatformColumnHeader>
                                    {[1, 2, 3, 4, 5].map((idx) => (
                                      <DropdownSkeletonItem key={`ig-skeleton-${idx}`}>
                                        <DropdownSkeletonCircle />
                                        <DropdownSkeletonText>
                                          <DropdownSkeletonLine $width="60%" />
                                          <DropdownSkeletonLine $width="40%" />
                                        </DropdownSkeletonText>
                                      </DropdownSkeletonItem>
                                    ))}
                                  </PlatformColumn>
                                  <PlatformColumn $isRight>
                                    <PlatformColumnHeader $platform="tiktok">
                                      <PlatformIcon platform="tiktok" size={11} />
                                      Results
                                    </PlatformColumnHeader>
                                    {[1, 2, 3, 4, 5].map((idx) => (
                                      <DropdownSkeletonItem key={`tt-skeleton-${idx}`}>
                                        <DropdownSkeletonCircle />
                                        <DropdownSkeletonText>
                                          <DropdownSkeletonLine $width="60%" />
                                          <DropdownSkeletonLine $width="40%" />
                                        </DropdownSkeletonText>
                                      </DropdownSkeletonItem>
                                    ))}
                                  </PlatformColumn>
                                </TwoColumnGrid>
                              ) : (quickAddInstagramSuggestions.length === 0 && quickAddTikTokSuggestions.length === 0) ? (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    No creators found for "@{quickAddHandle}"
                                  </div>
                                </div>
                              ) : (
                                <>
                                  
                                  {/* Two-column layout for Instagram and TikTok */}
                                  <TwoColumnGrid>
                                    {/* Instagram Column */}
                                    <PlatformColumn>
                                      <PlatformColumnHeader $platform="instagram">
                                        <PlatformIcon platform="instagram" size={11} />
                                        Results
                                      </PlatformColumnHeader>
                                      {quickAddInstagramSuggestions.length > 0 ? (
                                        quickAddInstagramSuggestions.map((suggestion, idx) => (
                                          <SuggestionItem
                                            key={`instagram-${suggestion.handle}-${idx}`}
                                            $selected={false}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleSelectSuggestion(suggestion, suggestion._existsInLibrary)}
                                          >
                                            {suggestion.profile_image_url ? (
                                              <SuggestionImage 
                                                src={suggestion.profile_image_url} 
                                                alt={suggestion.handle}
                                              />
                                            ) : (
                                              <SuggestionFallback $platform="instagram">
                                                {suggestion.handle?.charAt(0)?.toUpperCase() || '?'}
                                              </SuggestionFallback>
                                            )}
                                            <SuggestionInfo>
                                              <SuggestionHandle>
                                                @{suggestion.handle}
                                                {suggestion.is_verified && (
                                                  <VerifiedBadge>
                                                    <CheckOutlined />
                                                  </VerifiedBadge>
                                                )}
                                                {suggestion._existsInLibrary && (
                                                  <LibraryBadge>In library</LibraryBadge>
                                                )}
                                              </SuggestionHandle>
                                            </SuggestionInfo>
                                          </SuggestionItem>
                                        ))
                                      ) : (
                                        <SuggestionItem $selected={false} style={{ cursor: 'default', opacity: 0.5 }}>
                                          <SuggestionInfo>
                                            <SuggestionHandle style={{ color: '#9ca3af', fontSize: '11px' }}>
                                              No results
                                            </SuggestionHandle>
                                          </SuggestionInfo>
                                        </SuggestionItem>
                                      )}
                                    </PlatformColumn>
                                    
                                    {/* TikTok Column */}
                                    <PlatformColumn $isRight>
                                      <PlatformColumnHeader $platform="tiktok">
                                        <PlatformIcon platform="tiktok" size={11} />
                                        Results
                                      </PlatformColumnHeader>
                                      {quickAddTikTokSuggestions.length > 0 ? (
                                        quickAddTikTokSuggestions.map((suggestion, idx) => (
                                          <SuggestionItem
                                            key={`tiktok-${suggestion.handle}-${idx}`}
                                            $selected={false}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleSelectSuggestion(suggestion, suggestion._existsInLibrary)}
                                          >
                                            {suggestion.profile_image_url ? (
                                              <SuggestionImage 
                                                src={suggestion.profile_image_url} 
                                                alt={suggestion.handle}
                                              />
                                            ) : (
                                              <SuggestionFallback $platform="tiktok">
                                                {suggestion.handle?.charAt(0)?.toUpperCase() || '?'}
                                              </SuggestionFallback>
                                            )}
                                            <SuggestionInfo>
                                              <SuggestionHandle>
                                                @{suggestion.handle}
                                                {suggestion.is_verified && (
                                                  <VerifiedBadge>
                                                    <CheckOutlined />
                                                  </VerifiedBadge>
                                                )}
                                                {suggestion._existsInLibrary && (
                                                  <LibraryBadge>In library</LibraryBadge>
                                                )}
                                              </SuggestionHandle>
                                            </SuggestionInfo>
                                          </SuggestionItem>
                                        ))
                                      ) : (
                                        <SuggestionItem $selected={false} style={{ cursor: 'default', opacity: 0.5 }}>
                                          <SuggestionInfo>
                                            <SuggestionHandle style={{ color: '#9ca3af', fontSize: '11px' }}>
                                              No results
                                            </SuggestionHandle>
                                          </SuggestionInfo>
                                        </SuggestionItem>
                                      )}
                                    </PlatformColumn>
                                  </TwoColumnGrid>
                                </>
                              )}
                            </SuggestionsDropdown>
                          )}
                        </QuickAddContainer>
                      ) : (
                        <AddColumnContainer onClick={() => setShowQuickAdd(true)}>
                        <AddColumnContent>
                          <PlusIcon />
                          Add Creator
                        </AddColumnContent>
                      </AddColumnContainer>
                      )
                    ) : null}
                  </TableCell>
                  
                  {/* Email Sources Column Cell - Fixed right of creator (empty placeholder) */}
                  <TableCell $width={50}>
                    {/* Empty email sources cell */}
                  </TableCell>
                  
                  {/* Workflow Status Column Cell - empty placeholder when workflow is enabled */}
                  {workflowEnabled && (
                    <TableCell $width={140}>
                      {/* Empty workflow status cell */}
                    </TableCell>
                  )}
                  
                  {/* Actual column cells */}
                  {visibleColumns.map(column => (
                    <TableCell key={`empty-${column.id}-${index}`} $width={getColumnWidth(column.id)} $isOutreach={false}>
                      {/* Empty data cell */}
                    </TableCell>
                  ))}
                  <TableCell $width={180} $isLast>
                    {/* Empty action cell */}
                  </TableCell>
                </StyledTableRow>
              ))}
              
            </TableBody>
              </ScrollableTableContainer>
              
              {/* Custom Always-Visible Scrollbars */}
              <CustomScrollbar $vertical>
                <CustomScrollbarThumb 
                  $vertical
                  style={{ 
                    height: `${scrollThumbHeight}px`,
                    transform: `translateY(${scrollThumbTop}px)`
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setIsDraggingVertical(true);
                    const startY = e.clientY;
                    const startScrollTop = scrollContainerRef.current?.scrollTop || 0;
                    
                    const handleMouseMove = (moveEvent: MouseEvent) => {
                      if (!scrollContainerRef.current) return;
                      const deltaY = moveEvent.clientY - startY;
                      const scrollHeight = scrollContainerRef.current.scrollHeight;
                      const clientHeight = scrollContainerRef.current.clientHeight;
                      const scrollRatio = (scrollHeight - clientHeight) / (clientHeight - scrollThumbHeight);
                      scrollContainerRef.current.scrollTop = startScrollTop + (deltaY * scrollRatio);
                    };
                    
                    const handleMouseUp = () => {
                      setIsDraggingVertical(false);
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };
                    
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                />
              </CustomScrollbar>
              
              <CustomScrollbar>
                <CustomScrollbarThumb 
                  style={{ 
                    width: `${scrollThumbWidth}px`,
                    transform: `translateX(${scrollThumbLeft}px)`
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setIsDraggingHorizontal(true);
                    const startX = e.clientX;
                    const startScrollLeft = scrollContainerRef.current?.scrollLeft || 0;
                    
                    const handleMouseMove = (moveEvent: MouseEvent) => {
                      if (!scrollContainerRef.current) return;
                      const deltaX = moveEvent.clientX - startX;
                      const scrollWidth = scrollContainerRef.current.scrollWidth;
                      const clientWidth = scrollContainerRef.current.clientWidth;
                      const scrollRatio = (scrollWidth - clientWidth) / (clientWidth - scrollThumbWidth);
                      scrollContainerRef.current.scrollLeft = startScrollLeft + (deltaX * scrollRatio);
                    };
                    
                    const handleMouseUp = () => {
                      setIsDraggingHorizontal(false);
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };
                    
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                />
              </CustomScrollbar>

            </CustomTable>
            
            {/* Cell Details Sidebar - positioned relative to table only, not action bar */}
            {selectedCell && (
              <CellDetailsSidebar
                isOpen={cellSidebarOpen}
                cellData={selectedCell.data}
                column={selectedCell.column}
                onClose={onCloseCellSidebar || (() => {})}
                creator={selectedCell.creator}
                columns={columns}
                rowData={getCreatorRowData ? getCreatorRowData(selectedCell.creator || null) : {}}
                tableId={tableId}
                onVerifyPost={onVerifyPost}
                onRunFullRow={(creatorId) => {
                  // Run all executable columns for this creator
                  if (selectedCell.creator) {
                    columns.forEach(column => {
                      const canExecute = canColumnBeExecuted(column);
                      if (canExecute) {
                        // Small delay to avoid overwhelming the API
                        const delay = columns.indexOf(column) * 100;
                        setTimeout(() => {
                          onResearchCell(creatorId, column.id);
                        }, delay);
                      }
                    });
                  }
                }}
                onEditCreator={onEditCreator}
                onRemoveCreator={onRemoveCreator}
              />
            )}
            
            {/* Companion Draft Sidebar - positioned next to table */}
            <CompanionDraftSidebar
              isOpen={companionDraftOpen}
              creator={companionDraftCreator}
              onClose={() => {
                setCompanionDraftOpen(false);
                setCompanionDraftCreator(null);
              }}
              companionName={selectedProject?.companion_data?.id ? (getManagerById(selectedProject.companion_data.id)?.name || 'Campaign Carl') : 'Campaign Carl'}
              companionId={selectedProject?.companion_data?.id || 'campaign-carl'}
              companionColor={selectedProject?.companion_data?.id ? (getManagerById(selectedProject.companion_data.id)?.color || '#3b82f6') : '#3b82f6'}
              generatedDraft={companionDraftCreator ? companionDrafts.get(companionDraftCreator.id) : undefined}
              messages={companionDraftCreator ? companionMessages.get(companionDraftCreator.id) : undefined}
              conversationPreview={companionDraftCreator ? companionConversations.get(companionDraftCreator.id) : undefined}
              onRegenerateDraft={handleRegenerateDraft}
            />
            </TableAndSidebarWrapper>
          </TableContentWrapper>
        </TableWrapper>

      </MainContainer>
      
      {/* Creator Outreach Drawer - positioned outside padded container for full viewport */}
      {outreachDrawerContent}
      
      {/* Outreach Preview Modal */}
      {outreachPreviewOpen && outreachPreviewColumn && outreachPreviewCreator && outreachPreviewRowId && (
        <OutreachPreviewModal
          isOpen={outreachPreviewOpen}
          onClose={() => {
            setOutreachPreviewOpen(false);
            setOutreachPreviewColumn(null);
            setOutreachPreviewCreator(null);
            setOutreachPreviewRowId(null);
          }}
          column={outreachPreviewColumn}
          creator={outreachPreviewCreator}
          rowData={tableData[outreachPreviewRowId] || {}}
          allColumns={columns}
          outreachData={(() => {
            try {
              const value = tableData[outreachPreviewRowId]?.[outreachPreviewColumn.id]?.value;
              return value ? JSON.parse(value as string) : undefined;
            } catch (e) {
              return undefined;
            }
          })()}
          onSend={async (preview) => {
            if (!outreachPreviewCreator || !outreachPreviewColumn || !outreachPreviewRowId) return;
            
            setIsSendingEmail(true);
            try {
              const creatorEmail = outreachPreviewCreator.contacts?.find(c => c.type === 'email')?.value;
              if (!creatorEmail) {
                message.error('No email address found for this creator');
                return;
              }
              
              const result = await outreachService.sendEmail({
                creator_id: outreachPreviewCreator.id,
                to_email: creatorEmail,
                to_name: outreachPreviewCreator.tiktok_data?.[0]?.handle || outreachPreviewCreator.instagram_data?.[0]?.handle || '',
                subject: preview.subject,
                html_content: preview.html,
                plain_text: preview.plainText,
                send_from_email: outreachPreviewColumn.outreach_config?.send_from_email || 'noreply@example.com',
                send_from_name: outreachPreviewColumn.outreach_config?.send_from_name || 'Your Company',
                table_id: tableId || '',
                row_id: outreachPreviewRowId,
                column_id: outreachPreviewColumn.id
              });
              
              if (result.success) {
                message.success('Email sent successfully!');
                
                // Update cell data to mark as sent
                const outreachData: OutreachCellData = {
                  status: 'sent',
                  personalizedContent: preview.html,
                  subject: preview.subject,
                  sentAt: result.sent_at || new Date().toISOString(),
                  sentTo: creatorEmail
                };
                
                // Stringify the data for storage
                onUpdateCellValue?.(outreachPreviewRowId, outreachPreviewColumn.id, JSON.stringify(outreachData));
                
                // Close modal after successful send
                setOutreachPreviewOpen(false);
                setOutreachPreviewColumn(null);
                setOutreachPreviewCreator(null);
                setOutreachPreviewRowId(null);
              } else {
                message.error(`Failed to send email: ${result.error}`);
              }
            } catch (error) {
              console.error('Error sending email:', error);
              message.error('An unexpected error occurred');
            } finally {
              setIsSendingEmail(false);
            }
          }}
          isSending={isSendingEmail}
        />
      )}
      
       {/* Column Config Popover */}
       {columnConfigPopover.anchorEl && columnConfigPopover.position && (
         <div style={{ 
           position: 'fixed', 
           left: columnConfigPopover.position.left, 
           top: columnConfigPopover.position.top,
           width: 0,
           height: 0,
           zIndex: 1000
         }}>
           <ColumnConfigPopover
             column={columnConfigPopover.column}
             open={!!columnConfigPopover.anchorEl}
             onOpenChange={(open) => {
               if (!open) {
                 setColumnConfigPopover({ column: null, anchorEl: null });
               }
             }}
             onSave={(updatedColumn) => {
               onSaveColumn(updatedColumn);
               setColumnConfigPopover({ column: null, anchorEl: null });
             }}
             onCancel={() => {
               setColumnConfigPopover({ column: null, anchorEl: null });
             }}
           />
         </div>
       )}
    </TableContainer>
    </div>
  );
};

